(function( $ ){
    /**
     * @typedef {Object} TiledImageInfo
     * @property {Number} TiledImageInfo.id
     * @property {Number[]} TiledImageInfo.shaderOrder
     * @property {Object} TiledImageInfo.shaders
     * @property {Object} TiledImageInfo.drawers
     */

    /**
     * One vector mesh.
     *
     * @typedef {object} VectorMesh
     * @property {Float32Array} vertices - Packed vertices as vec4(x, y, depth, textureId).
     * @property {Uint32Array} indices - Indices into the vertex array.
     * @property {number[]} [color] - Constant RGBA color used when parameters are absent.
     * @property {Float32Array} [parameters] - Per-vertex payload. For icons: vec4(xStart, yStart, width, height).
     * @property {number} [lineWidth=1] - Native line width in pixels. Used only for `linePrimitives`.
     */

    /**
     * Tessellated vector payload for one tile.
     *
     * Icons are represented as point meshes. A point mesh is rendered as an icon
     * when its vertex textureId component is >= 0 and its `parameters` array
     * contains per-vertex atlas placement data.
     *
     * @typedef {object} VectorMeshTile
     * @property {VectorMesh[]} [fills] - Polygon fill triangle meshes.
     * @property {VectorMesh[]} [lines] - Stroke triangle meshes rendered with gl.TRIANGLES. Mutually exclusive with linePrimitives.
     * @property {VectorMesh[]} [linePrimitives] - Native line segment meshes rendered with gl.LINES. Mutually exclusive with lines.
     * @property {VectorMesh[]} [points] - Point marker and icon quad meshes.
     */

    /**
     * Host-supplied HTTP transport used by FlexDrawer-owned workers (MVT, GeoJSON).
     *
     * The adapter is a fetch-compatible shim: when supplied, every network request
     * the library would otherwise issue with `fetch(url)` is routed through it.
     * Implementations must support method, headers, body, signal, Range headers,
     * and binary responses (.arrayBuffer / .blob / .body).
     *
     * Adapters are resolved in this order:
     *   1. explicit `httpAdapter` constructor option on the tile source,
     *   2. drawer-level `httpAdapter` captured into `FlexDrawer._defaultHttpAdapter`,
     *   3. null — the library falls back to native `fetch`.
     *
     * @typedef {object} HttpAdapter
     * @property {(url: string, init?: RequestInit) => Promise<Response>} fetch
     */

    /**
     * @property {Number} idGenerator unique ID getter
     *
     * @class OpenSeadragon.FlexDrawer
     * @classdesc implementation of WebGL renderer for an {@link OpenSeadragon.Viewer}
     */
    class FlexDrawer extends OpenSeadragon.DrawerBase {
        /**
         * @param {Object} options options for this Drawer
         * @param {OpenSeadragon.Viewer} options.viewer the Viewer that owns this Drawer
         * @param {OpenSeadragon.Viewport} options.viewport reference to Viewer viewport
         * @param {HTMLElement} options.element parent element
         * @param {[String]} options.debugGridColor see debugGridColor in {@link OpenSeadragon.Options} for details
         * @param {Object} options.options optional
         */
        constructor(options){
            super(options);

            this._destroyed = false;
            this._imageSmoothingEnabled = false; // will be updated by setImageSmoothingEnabled
            this._configuredExternally = false;
            this._managedShaderSourceSlots = new Map();
            this._managedShaderSourceNextIndex = null;
            // We have 'undefined' extra format for blank tiles
            this._supportedFormats = ["rasterBlob", "context2d", "image", "imageBitmap", "vector-mesh", "gpuTextureSet", "undefined"];
            this.rebuildCounter = 0;

            // Capture the host-supplied HttpAdapter as a process-wide fallback so tile sources
            // instantiated outside the drawer (OSD-managed paths) can still pick it up.
            // Explicit per-tile-source `httpAdapter` options take precedence.
            if (this.options.httpAdapter) {
                FlexDrawer._defaultHttpAdapter = this.options.httpAdapter;
            }

            this._suspendRenderingDepth = 0;
            this._pendingRebuildRequest = null;
            this._drawReady = false;

            this._interactionOptions = this._normalizeInteractionOptions(this.options.interaction);
            this._interactionEnabled = false;
            this._interactionListeners = null;
            this._interactionDragActive = false;
            this._interactionMouseNavCaptured = false;
            this._interactionPreviousMouseNavEnabled = null;
            this._interactionGestureSettingsCaptured = false;
            this._interactionPreviousGestureSettings = null;
            // shader types already reported by _warnOnMissingInteractionForwarding(), so a rebuild
            // loop does not spam the console; cleared when forwarding is turned on. Created by
            // _interactionWarnedTypes(), which may run before this line (see there).
            this._interactionForwardingWarnedTypes = this._interactionForwardingWarnedTypes || null;

            // reject listening for the tile-drawing and tile-drawn events, which this drawer does not fire
            this.viewer.rejectEventHandler("tile-drawn", "The WebGLDrawer does not raise the tile-drawn event");
            this.viewer.rejectEventHandler("tile-drawing", "The WebGLDrawer does not raise the tile-drawing event");
            this.viewer.world.addHandler("remove-item", (e) => {
                const tiledImage = e.item;
                if (tiledImage && tiledImage.__flexManagedShaderSourceSlotKey) {
                    const slot = this._managedShaderSourceSlots.get(tiledImage.__flexManagedShaderSourceSlotKey);
                    if (slot && slot.item === tiledImage) {
                        slot.item = null;
                    }
                }
                // if managed internally on the instance (regardless of renderer state), handle removal
                if (tiledImage.__shaderConfig) {
                    this.renderer.removeShader(tiledImage.__shaderConfig.id);
                    delete tiledImage.__shaderConfig;
                    if (tiledImage.__wglCompositeHandler) {
                        tiledImage.removeHandler('composite-operation-change', tiledImage.__wglCompositeHandler);
                    }
                }
                // The removed image may have been the only float-precision source in the world.
                // Runs after OSD has already spliced it out of world._items.
                this._refreshDataPrecision();

                // if now managed externally, just request rebuild, also updates order
                if (!this._configuredExternally) {
                    // Update keys
                    this._requestRebuild();
                }
            });
        } // end of constructor

        /**
         * Drawer type.
         * @returns {String}
         */
        getType() {
            return 'flex-renderer';
        }

        getSupportedDataFormats() {
            return this._supportedFormats;
        }

        getRequiredDataFormats() {
            return this._supportedFormats;
        }

        get defaultOptions() {
            return {
                usePrivateCache: true,
                preloadCache: true,
                copyShaderConfig: false,
                handleNavigator: true,
                shaderSourceResolver: null,
                httpAdapter: null,
                sharedContextKey: null,
                interaction: false,
                // "auto" | "unorm8" | "float16" — precision of the first-pass color target.
                // See FlexRendererOptions.precision. Default off: "auto" lets the *data* upgrade
                // the target to RGBA16F, which doubles the offscreen color array, so turning the
                // negotiation on is a deployment decision.
                precision: "unorm8",
                // hex bg color, by default transparent. This is the SOURCE colour: it is baked
                // into the second-pass fragment shader as the seed of the layer composition, so
                // it decides what the stack starts from (and therefore the alpha it emits).
                backgroundColor: undefined,
                // [r,g,b,a] in 0..1, default undefined -> the renderer's [1,1,1,1] opaque white.
                // This is the DESTINATION colour: a gl.clear of the output surface before the
                // second pass blends onto it. Distinct from backgroundColor and not derivable
                // from it - the second pass composites premultiplied (ONE/ONE_MINUS_SRC_ALPHA),
                // so a pass that draws nothing reads as this, whatever the shader seed was.
                // Construction-time only; the renderer exposes a getter but no setter. Pass
                // [0,0,0,0] for output with a real alpha channel. See OFFSCREEN.md.
                presentationClearColor: undefined
            };
        }

        /**
         * Override the default configuration: the renderer will use given shaders,
         * supplied with data from collection of TiledImages, to render.
         * TiledImages are treated only as data sources, the rendering outcome is fully in controls of the shader specs.
         * @param {Object.<string, ShaderLayerConfig>} shaders map of id -> shader config value
         * @param {Array<string>} [shaderOrder=undefined] custom order of shader ids to render.
         * @param {Object} [options]
         * @param {Boolean} [options.immediate=false] if true, run the rebuild synchronously
         *      (program registration + dimensions update) instead of deferring via setTimeout.
         *      Required when the caller intends to draw immediately after configuring.
         * @return {OpenSeadragon.Promise} promise resolved when the renderer gets rebuilt
         */
        overrideConfigureAll(shaders, shaderOrder = undefined, options = {}) {
            // todo reset also when reordering tiled images!
            // or we could change order only

            if (this.options.handleNavigator && this.viewer.navigator) {
                this.viewer.navigator.drawer.overrideConfigureAll(shaders, shaderOrder, options);
            }

            const willBeConfigured = !!shaders;
            if (!willBeConfigured) {
                if (this._configuredExternally) {
                    this._configuredExternally = false;
                    // If we changed render style, recompile everything
                    this.renderer.deleteShaders();
                    return $.Promise.all(this.viewer.world._items.map(item => this.tiledImageCreated(item).id));
                }
                return $.Promise.resolve();
            }

            // If custom rendering used, use arbitrary external configuration
            this._configuredExternally = true;
            this.renderer.deleteShaders();

            const requestedOrder = shaderOrder || Object.keys(shaders);
            const createdOrder = [];

            for (const shaderId of requestedOrder) {
                const sanitized = $.FlexRenderer.sanitizeKey(shaderId);
                if (this.renderer._shaders[sanitized]) {
                    this.renderer.removeShader(sanitized);
                }
                const shader = this.renderer.createShaderLayer(shaderId, shaders[shaderId], true);
                if (shader) {
                    createdOrder.push(shaderId);
                }
            }
            this.renderer.setShaderLayerOrder(createdOrder);

            this.renderer.notifyVisualizationChanged({
                reason: "external-config",
                external: true
            });

            return this._requestRebuild(0, false, false, !!(options && options.immediate));
        }

        /**
         * Retrieve shader config by its key. Shader IDs are known only
         * when overrideConfigureAll() called
         * @param key
         * @return {ShaderLayerConfig|*|undefined}
         */
        getOverriddenShaderConfig(key) {
            const shaderLayer = this.renderer.getAllShaders()[key];
            return shaderLayer ? shaderLayer.getConfig() : undefined;
        }

        /**
         * If shaders are managed internally, tiled image can be configured a single custom
         * shader if desired. This shader is ignored if overrideConfigureAll({...}) used.
         * @param {OpenSeadragon.TiledImage} tiledImage
         * @param {ShaderLayerConfig} shader
         * @return {ShaderLayerConfig} shader config used, a copy if options.copyShaderConfig is true, otherwise a modified argument
         */
        configureTiledImage(tiledImage, shader) {
            if (this.options.copyShaderConfig) {
                shader = $.extend(true, {}, shader);
            }

            shader.id = shader.id || (tiledImage.__shaderConfig && tiledImage.__shaderConfig.id) || this.constructor.idGenerator;
            tiledImage.__shaderConfig = shader;

            // if already configured, request re-configuration
            if (tiledImage.__wglCompositeHandler) {
                this.tiledImageCreated(tiledImage);
            }

            if (this.options.handleNavigator && this.viewer.navigator) {
                const nav = this.viewer.navigator;
                let tiledImageNavigator = null;
                for (let i = 0; i < nav.world.getItemCount(); i++) {
                    if (nav.world.getItemAt(i).source === tiledImage.source) {
                        tiledImageNavigator = nav.world.getItemAt(i);
                        break;
                    }
                }

                if (tiledImageNavigator) {
                    this.viewer.navigator.drawer.configureTiledImage(tiledImageNavigator, shader);
                } else {
                    $.console.warn("Could not find corresponding tiled image for the navigator!");
                }
            }

            this.renderer.notifyVisualizationChanged({
                reason: "configure-tiled-image",
                shaderId: shader.id
            });

            return shader;
        }

        /**
         * Register TiledImage into the system.
         * @param {OpenSeadragon.TiledImage} tiledImage
         * @return {OpenSeadragon.Promise} promise resolved when the renderer gets rebuilt
         */
        tiledImageCreated(tiledImage) {
            // Always attempt to clean up
            if (tiledImage.__wglCompositeHandler) {
                tiledImage.removeHandler('composite-operation-change', tiledImage.__wglCompositeHandler);
            }

            this._captureDeclaredDataPrecision(tiledImage);

            if (tiledImage.__flexManagedShaderSourceSlotKey) {
                return this._requestRebuild();
            }

            // If we configure externally the renderer, simply bypass
            if (this._configuredExternally) {
                // __shaderConfig reference is kept only when managed internally, can keep custom shader config for particular tiled image
                delete tiledImage.__shaderConfig;
                return this._requestRebuild();
            }

            let config = tiledImage.__shaderConfig;
            if (!config) {
                config = tiledImage.__shaderConfig = {
                    name: "Identity shader",
                    type: "identity",
                    visible: 1,
                    fixed: false,
                    params: {},
                    cache: {},
                };
            }

            if (!config.id) {
                // potentially problematic, relies on the fact that navigator is always initialized second
                // shared ID are required for controls, which have only 1 present HTML but potentially two listeners
                if (this._isNavigatorDrawer) {
                    const parent = this.viewer.viewer;
                    for (let i = 0; i < parent.world.getItemCount(); i++) {
                        const tiledImageParent = parent.world.getItemAt(i);
                        if (tiledImageParent.source === tiledImage.source) {
                            // Parent's __shaderConfig may be missing during a reset window
                            // (tiledImageCreated deletes it when _configuredExternally is true).
                            // Fall through to idGenerator in that case.
                            if (tiledImageParent.__shaderConfig) {
                                config.id = tiledImageParent.__shaderConfig.id;
                            }
                            break;
                        }
                    }
                }
                if (!config.id) {
                    // generate a unique ID for the shader
                    config.id = this.constructor.idGenerator;
                }
            }

            const shaderId = config.id;

            // When this._configuredExternally == false, the index is always self index, deduced dynamically
            const property = Object.getOwnPropertyDescriptor(config, 'tiledImages');
            if (!property || property.configurable) {
                delete config.tiledImages;

                // todo make custom renderer pass tiledImages as array of tiled images -> will deduce easily
                Object.defineProperty(config, "tiledImages", {
                    get: () => [this.viewer.world.getIndexOfItem(tiledImage)]
                });
            } // else already set as a getter


            if (!config.params.use_blend && tiledImage.compositeOperation) {
                // eslint-disable-next-line camelcase
                config.params.use_mode = 'blend';
                // eslint-disable-next-line camelcase
                config.params.use_blend = tiledImage.compositeOperation;
            }

            tiledImage.__wglCompositeHandler = e => {
                const shader = this.renderer.getShaderLayer(shaderId);
                const config = shader.getConfig();
                const operation = tiledImage.compositeOperation;
                if (operation) {
                    // eslint-disable-next-line camelcase
                    config.params.use_blend = operation;
                    // eslint-disable-next-line camelcase
                    config.params.use_mode = 'blend';
                } else {
                    // eslint-disable-next-line camelcase
                    delete config.params.use_blend;
                    // eslint-disable-next-line camelcase
                    config.params.use_mode = 'show';
                }
                shader.resetMode(config.params, true);
                this._requestRebuild(0);
            };

            tiledImage.addHandler('composite-operation-change', tiledImage.__wglCompositeHandler);

            // copy config only applied when passed externally
            this.renderer.createShaderLayer(shaderId, config, false);
            return this._requestRebuild();
        }

        /**
         * Rebuild current shaders to reflect updated configurations.
         * @return {Promise}
         */
        rebuild() {
            if (this.options.handleNavigator) {
                // Callers like UTILITIES.changeModeOfLayer, setFilterOfLayer, the
                // shaderSideMenu visibility toggle, and shader-order reorders mutate
                // only the main shader's config/params and then call rebuild().
                // Mirror that state into the navigator's shader instances first so
                // its rebuild doesn't re-emit the previous (stale) configuration.
                this._syncNavigatorShaderState();
                this.viewer.navigator.drawer.rebuild();
            }
            return this._requestRebuild();
        }

        _applyShaderConfigMutationRequest(request = {}, syncNavigator = true) {
            const {
                shaderId,
                mutation,
                refreshShader = true,
                rebuildProgram = true,
                rebuildDrawer = true,
                resetItems = true,
                reason = "shader-config-mutation"
            } = request;

            if (!shaderId) {
                return $.Promise.resolve();
            }

            const shader = this.renderer.getShaderLayer(shaderId);
            if (!shader) {
                return $.Promise.resolve();
            }

            const config = shader.getConfig();
            if (typeof mutation === "function") {
                mutation(config, shader);
            } else if (mutation && typeof mutation === "object") {
                Object.assign(config, mutation);
            }

            if (refreshShader) {
                this.renderer.refreshShaderLayer(shaderId, { rebuildProgram });
            } else if (rebuildProgram) {
                try {
                    this.renderer.registerProgram(null, this.renderer.backend.secondPassProgramKey);
                } catch (e) {
                    // The config mutation stands; the previously linked program keeps rendering
                    // until a later rebuild succeeds.
                    $.console.error(`[flex-renderer] shader '${shaderId}' mutation could not rebuild ` +
                        `the second-pass program; the previous program is kept.`, e);
                }
            }

            this.renderer.notifyVisualizationChanged({
                reason,
                shaderId,
                shaderType: shader.constructor.type()
            });

            if (
                syncNavigator &&
                !request.drawerLocalWorldIndex &&
                this.options.handleNavigator &&
                this.viewer.navigator &&
                this.viewer.navigator.drawer
            ) {
                this.viewer.navigator.drawer._applyShaderConfigMutationRequest(request, false);
            }

            if (resetItems && this.viewer.world && typeof this.viewer.world.resetItems === "function") {
                this.viewer.world.resetItems();
            }

            if (rebuildDrawer) {
                return this._requestRebuild(0, true);
            }
            this.viewer.forceRedraw();
            return $.Promise.resolve();
        }

        /**
         * Mirror control state (IControl.encoded) from the main drawer's shaders into
         * the navigator drawer's shader instances. Required because shader-internal
         * UI controls (color picker, range sliders, etc.) mutate the main shader's
         * controls directly via `owner.invalidate()` and never reach the navigator,
         * whose shader instances are separate and have no DOM-bound handlers.
         *
         * Without this, fast control-driven changes leave the navigator's render
         * with stale uniforms/params until something else (e.g. _applyShaderConfigMutationRequest)
         * resyncs it. Visualization-change events from nav-side `set()` calls are
         * suppressed so consumers don't see duplicates.
         */
        _syncNavigatorShaderState() {
            const nav = this.viewer.navigator;
            if (!nav || !nav.drawer || !nav.drawer.renderer) {
                return;
            }

            // Mirror non-control state that bypasses _controls entirely:
            //   - config.visible (layer checkbox in shaderSideMenu)
            //   - config.params.use_mode / use_blend (UTILITIES.changeModeOfLayer, onChangeBlend)
            //   - config.params.use_<filter>         (UTILITIES.setFilterOfLayer)
            // For mode/blend we re-run resetMode on the nav shader; for filters, resetFilters.
            // Both are idempotent and read directly from the params object.
            const syncConfig = (mainShader, navShader) => {
                const mainConfig = typeof mainShader.getConfig === "function" ? mainShader.getConfig() : null;
                const navConfig = typeof navShader.getConfig === "function" ? navShader.getConfig() : null;
                if (!mainConfig || !navConfig || mainConfig === navConfig) {
                    return;
                }

                if (navConfig.visible !== mainConfig.visible) {
                    navConfig.visible = mainConfig.visible;
                }

                const mainParams = mainConfig.params;
                if (!mainParams) {
                    return;
                }
                navConfig.params = navConfig.params || {};
                const navParams = navConfig.params;

                let modeOrBlendChanged = false;
                let filterChanged = false;
                for (const key in mainParams) {
                    if (!key.startsWith("use_")) {
                        continue;
                    }
                    if (navParams[key] === mainParams[key]) {
                        continue;
                    }
                    navParams[key] = mainParams[key];
                    if (key === "use_mode" || key === "use_blend") {
                        modeOrBlendChanged = true;
                    } else {
                        filterChanged = true;
                    }
                }

                if (modeOrBlendChanged && typeof navShader.resetMode === "function") {
                    try {
                        navShader.resetMode(navParams, true, false);
                    } catch (e) {
                        $.console.warn("FlexDrawer: nav resetMode failed", mainShader.id, e);
                    }
                }
                if (filterChanged && typeof navShader.resetFilters === "function") {
                    try {
                        navShader.resetFilters(navParams, true, false);
                    } catch (e) {
                        $.console.warn("FlexDrawer: nav resetFilters failed", mainShader.id, e);
                    }
                }
            };

            // Controls whose encoded state is an array (advanced slider breaks, custom colormap
            // palettes) never compare equal by identity, so a strict === here would re-set them
            // on every redraw. One level of element comparison is enough: no control encodes
            // nested arrays.
            const encodedEquals = (a, b) => {
                if (a === b) {
                    return true;
                }
                if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
                    return false;
                }
                return a.every((value, i) => value === b[i]);
            };

            const syncControls = (mainShader, navShader) => {
                if (!mainShader || !navShader || !mainShader._controls || !navShader._controls) {
                    return;
                }
                for (const controlName in mainShader._controls) {
                    const mainControl = mainShader._controls[controlName];
                    const navControl = navShader._controls[controlName];
                    if (!navControl || typeof navControl.set !== "function" || !mainControl) {
                        continue;
                    }
                    if (mainControl.encoded === undefined) {
                        continue;
                    }
                    if (encodedEquals(navControl.encoded, mainControl.encoded)) {
                        continue;
                    }

                    const prevSuppress = navControl._suppressVisualizationChanged;
                    navControl._suppressVisualizationChanged = true;
                    try {
                        navControl.set(mainControl.encoded);
                    } catch (e) {
                        $.console.warn(
                            "FlexDrawer: failed to sync navigator control state",
                            mainShader.id, controlName, e
                        );
                    } finally {
                        navControl._suppressVisualizationChanged = prevSuppress;
                    }
                }
            };

            // Group shaders (e.g. flex-layers/group.js) hold nested children under
            // shader.shaderLayers (same map shape, keyed by sanitized id). renderer.getAllShaders()
            // only returns the top-level map, so we walk recursively.
            const walk = (mainMap, navMap) => {
                if (!mainMap || !navMap) {
                    return;
                }
                for (const id in mainMap) {
                    const mainShader = mainMap[id];
                    const navShader = navMap[id];
                    if (!mainShader || !navShader) {
                        continue;
                    }
                    syncConfig(mainShader, navShader);
                    syncControls(mainShader, navShader);
                    if (mainShader.shaderLayers && navShader.shaderLayers) {
                        walk(mainShader.shaderLayers, navShader.shaderLayers);
                    }
                }
            };

            walk(this.renderer.getAllShaders(), nav.drawer.renderer.getAllShaders());
        }

        _handleRefetchRequest(request = undefined) {
            if (!request) {
                return this.viewer.world.resetItems();
            }

            if (request.kind === "shader-source-request") {
                return this._handleShaderSourceRequest(request);
            }

            if (request.kind === "shader-config-mutation") {
                return this._applyShaderConfigMutationRequest(request);
            }

            return this.viewer.world.resetItems();
        }

        _getManagedShaderSourceSlotKey(request = {}) {
            return `${request.shaderId || "shader"}:${Number.parseInt(request.sourceIndex, 10) || 0}`;
        }

        _allocateManagedShaderSourceWorldIndex() {
            const worldCount = this.viewer && this.viewer.world ? this.viewer.world.getItemCount() : 0;
            if (!Number.isInteger(this._managedShaderSourceNextIndex)) {
                this._managedShaderSourceNextIndex = worldCount;
            } else {
                this._managedShaderSourceNextIndex = Math.max(this._managedShaderSourceNextIndex, worldCount);
            }
            return this._managedShaderSourceNextIndex++;
        }

        _isManagedShaderSourceDescriptor(entry) {
            return !!(entry && typeof entry === "object" && (
                entry.tileSource !== undefined ||
                entry.source !== undefined ||
                entry.open !== undefined ||
                entry.openOptions !== undefined
            ));
        }

        _normalizeManagedShaderSourceDescriptor(entry = {}) {
            const descriptor = $.extend(true, {}, entry);
            const openOptions = $.extend(true, {},
                descriptor.openOptions || descriptor.open || {}
            );
            const tileSource = descriptor.tileSource !== undefined ? descriptor.tileSource : descriptor.source;

            delete descriptor.openOptions;
            delete descriptor.open;
            delete descriptor.tileSource;
            delete descriptor.source;

            return {
                tileSource,
                openOptions,
                meta: descriptor
            };
        }

        _openManagedShaderSourceAtSlot(slot, descriptor, request = {}) {
            const normalized = this._normalizeManagedShaderSourceDescriptor(descriptor);
            if (normalized.tileSource === undefined) {
                return $.Promise.reject(new Error("Managed shader source descriptor requires tileSource or source."));
            }

            const shader = request.shaderId ? this.renderer.getShaderLayer(request.shaderId) : null;
            const sourceIndex = Number.parseInt(request.sourceIndex, 10) || 0;
            const referenceItem = shader && typeof shader.getSourceTiledImage === "function"
                ? shader.getSourceTiledImage(sourceIndex)
                : null;

            const openOptions = $.extend(true, {
                opacity: 0,
                preload: false,
                preserveViewport: true
            }, normalized.openOptions || {}, {
                tileSource: normalized.tileSource
            });

            delete openOptions.index;
            delete openOptions.replace;

            if (referenceItem) {
                const bounds = referenceItem.getBoundsNoRotate(true);

                if (openOptions.x === undefined && openOptions.y === undefined && !openOptions.position) {
                    openOptions.x = bounds.x;
                    openOptions.y = bounds.y;
                }

                if (openOptions.width === undefined && openOptions.height === undefined) {
                    openOptions.width = bounds.width;
                }

                if (openOptions.clip === undefined && referenceItem.getClip) {
                    const clip = referenceItem.getClip();
                    if (clip) {
                        openOptions.clip = clip;
                    }
                }

                if (openOptions.rotation === undefined && typeof referenceItem.getRotation === "function") {
                    openOptions.rotation = referenceItem.getRotation();
                }

                if (openOptions.flipped === undefined && typeof referenceItem.getFlip === "function") {
                    openOptions.flipped = referenceItem.getFlip();
                }
            }

            return new $.Promise((resolve, reject) => {
                const success = openOptions.success;
                const error = openOptions.error;

                openOptions.success = (event) => {
                    const item = event && event.item ? event.item : null;
                    const worldIndex = item && this.viewer.world
                        ? this.viewer.world.getIndexOfItem(item)
                        : -1;

                    if (item) {
                        item.__flexManagedShaderSourceSlotKey = slot.key;
                        slot.item = item;
                        slot.worldIndex = worldIndex;
                    }

                    if (typeof success === "function") {
                        success(event);
                    }

                    resolve({
                        worldIndex,
                        tiledImage: item
                    });
                };

                openOptions.error = (event) => {
                    if (typeof error === "function") {
                        error(event);
                    }
                    reject(new Error(event && event.message ? event.message : "Failed to open managed shader source."));
                };

                this.viewer.addTiledImage(openOptions);
            });
        }

        realizeShaderSourceDescriptor(request = {}, descriptor = undefined) {
            const entry = descriptor === undefined ? request.entry : descriptor;
            if (!this._isManagedShaderSourceDescriptor(entry)) {
                return $.Promise.resolve(null);
            }

            const slotKey = this._getManagedShaderSourceSlotKey(request);
            let slot = this._managedShaderSourceSlots.get(slotKey);
            if (!slot) {
                slot = {
                    key: slotKey,
                    worldIndex: this._allocateManagedShaderSourceWorldIndex(),
                    item: null
                };
                this._managedShaderSourceSlots.set(slotKey, slot);
            }

            return this._openManagedShaderSourceAtSlot(slot, entry, request).then(result => ({
                worldIndex: result.worldIndex,
                refreshShader: false,
                rebuildProgram: false,
                rebuildDrawer: true,
                resetItems: false,
                drawerLocalWorldIndex: true
            }));
        }

        _resolveSourceRequestResult(request, result) {
            if (result === undefined || result === null || result === false) {
                return null;
            }

            if (Number.isInteger(result)) {
                return {
                    mutation: (config) => {
                        const tiledImages = Array.isArray(config.tiledImages) ? config.tiledImages.slice() : [];
                        tiledImages[request.sourceIndex || 0] = result;
                        config.tiledImages = tiledImages;
                    }
                };
            }

            if (Array.isArray(result)) {
                return {
                    mutation: (config) => {
                        config.tiledImages = result.slice();
                    }
                };
            }

            if (typeof result === "object") {
                if (Array.isArray(result.tiledImages)) {
                    return {
                        ...result,
                        mutation: result.mutation || ((config) => {
                            config.tiledImages = result.tiledImages.slice();
                        })
                    };
                }
                if (Number.isInteger(result.worldIndex)) {
                    return {
                        ...result,
                        mutation: result.mutation || ((config) => {
                            const tiledImages = Array.isArray(config.tiledImages) ? config.tiledImages.slice() : [];
                            tiledImages[request.sourceIndex || 0] = result.worldIndex;
                            config.tiledImages = tiledImages;
                        })
                    };
                }
                if (typeof result.mutation === "function") {
                    return result;
                }
            }

            return null;
        }

        _handleShaderSourceRequest(request = {}) {
            const shader = request.shaderId ? this.renderer.getShaderLayer(request.shaderId) : null;
            if (!shader) {
                return $.Promise.resolve();
            }

            const directWorldIndex = Number.parseInt(request.entry, 10);
            if (Number.isFinite(directWorldIndex) && String(directWorldIndex) === String(request.entry).trim()) {
                return this._applyShaderConfigMutationRequest({
                    ...request,
                    kind: "shader-config-mutation",
                    mutation: (config) => {
                        const tiledImages = Array.isArray(config.tiledImages) ? config.tiledImages.slice() : [];
                        tiledImages[request.sourceIndex || 0] = directWorldIndex;
                        config.tiledImages = tiledImages;
                    },
                    reason: request.reason || "shader-source-request",
                    refreshShader: request.refreshShader !== false,
                    rebuildProgram: request.rebuildProgram !== false,
                    rebuildDrawer: request.rebuildDrawer !== false,
                    resetItems: request.resetItems !== false
                });
            }

            if (this._isManagedShaderSourceDescriptor(request.entry)) {
                return this.realizeShaderSourceDescriptor(request).then(resolved => {
                    if (!resolved) {
                        return $.Promise.resolve();
                    }
                    const mutationSpec = this._resolveSourceRequestResult(request, resolved);
                    if (!mutationSpec) {
                        return $.Promise.resolve();
                    }
                    return this._applyShaderConfigMutationRequest({
                        ...request,
                        ...resolved,
                        ...mutationSpec,
                        kind: "shader-config-mutation",
                        reason: request.reason || resolved.reason || "shader-source-request"
                    });
                });
            }

            const resolver = this.options.shaderSourceResolver;
            if (typeof resolver !== "function") {
                $.console.warn("Shader source request received but no drawer.options.shaderSourceResolver is configured.", request);
                return $.Promise.resolve();
            }

            const outcome = resolver({
                request,
                drawer: this,
                viewer: this.viewer,
                renderer: this.renderer,
                shader,
                shaderConfig: shader.getConfig()
            });

            return $.Promise.resolve(outcome).then(result => {
                if (this._isManagedShaderSourceDescriptor(result)) {
                    return this.realizeShaderSourceDescriptor(request, result).then(realized =>
                        realized ? (() => {
                            const mutationSpec = this._resolveSourceRequestResult(request, realized);
                            if (!mutationSpec) {
                                return $.Promise.resolve();
                            }
                            return this._applyShaderConfigMutationRequest({
                                ...request,
                                ...realized,
                                ...mutationSpec,
                                kind: "shader-config-mutation",
                                reason: request.reason || realized.reason || "shader-source-request"
                            });
                        })() : $.Promise.resolve()
                    );
                }

                const resolved = this._resolveSourceRequestResult(request, result);
                if (!resolved) {
                    return $.Promise.resolve();
                }

                return this._applyShaderConfigMutationRequest({
                    ...request,
                    ...resolved,
                    kind: "shader-config-mutation",
                    reason: request.reason || resolved.reason || "shader-source-request",
                    refreshShader: resolved.refreshShader !== false,
                    rebuildProgram: resolved.rebuildProgram !== false,
                    rebuildDrawer: resolved.rebuildDrawer !== false,
                    resetItems: resolved.resetItems !== false
                });
            });
        }

        /**
         * This methods can suspend viewer animation, for example when
         * you are still in the process of modifying the viewer state
         * and the viewer is forced to re-render unfinished configuration(s).
         * @param reason
         */
        suspendRendering(reason = "manual") {
            this._suspendRenderingDepth++;
            this._drawReady = false;
            if (this._rebuildHandle) {
                clearTimeout(this._rebuildHandle);
                this._rebuildHandle = null;
            }
        }

        resumeRendering(reason = "manual") {
            if (this._suspendRenderingDepth > 0) {
                this._suspendRenderingDepth--;
            }
            if (this._suspendRenderingDepth > 0) {
                return;
            }

            const pending = this._pendingRebuildRequest;
            this._pendingRebuildRequest = null;

            if (pending) {
                this._requestRebuild(pending.timeout, pending.force, true);
            } else {
                this._refreshDrawReadyState();
                this.viewer.forceRedraw();
            }
        }

        _isRenderingSuspended() {
            return this._suspendRenderingDepth > 0;
        }

        _refreshDrawReadyState() {
            const canvas = this.canvas;
            this._drawReady = !this._isRenderingSuspended() &&
                !!canvas &&
                canvas.width > 0 &&
                canvas.height > 0 &&
                !this._hasInvalidBuildState();
            return this._drawReady;
        }


        /**
         * Clean up the FlexDrawer, removing all resources.
         */
        destroy() {
            if (this._destroyed) {
                return;
            }

            if (this._interactionEnabled) {
                this.setInteractionOptions({
                    enabled: false
                }, {
                    notify: false,
                    redraw: false,
                    reason: "drawer-destroy"
                });
            } else {
                this._detachInteractionListeners();
                this._resetInteractionTracking();
                this._releaseInteractionViewerInputCapture();
            }

            // WebGL resource cleanup is owned by FlexRenderer and the active backend.

            // unbind our event listeners from the viewer
            this.viewer.removeHandler("resize", this._resizeHandler);

            if (!this.options.offScreen) {
                this.container.removeChild(this.canvas);
                if (this.viewer.drawer === this){
                    this.viewer.drawer = null;
                }
            }

            if (this._rebuildHandle) {
                clearTimeout(this._rebuildHandle);
                this._rebuildHandle = null;
            }

            if (this._deferredRedrawHandle) {
                clearTimeout(this._deferredRedrawHandle);
                this._deferredRedrawHandle = null;
            }

            this.renderer.destroy();
            this.renderer = null;

            // set our destroyed flag to true
            this._destroyed = true;
        }

        // todo documment
        getPackCount(ti) {
            const world = this.viewer.world;
            if (!world) {
                return 1;
            }

            let tiledImage = ti;
            if (typeof ti === "number") {
                tiledImage = world.getItemAt(ti);
            }
            if (!tiledImage) {
                return 1;
            }

            return tiledImage.__flexPackCount || 1;
        }

        /**
         * Components carried by one texture-array layer of this image: 4 for RGBA8/RGBA16F,
         * 2 for RG16F, 1 for R16F. Defaults to 4 until tile metadata arrives, which is what
         * every format supported before the narrow ones carried anyway.
         * @param {OpenSeadragon.TiledImage|number} ti tiled image or its world index
         * @return {number}
         */
        getComponentsPerPack(ti) {
            const world = this.viewer.world;
            if (!world) {
                return 4;
            }

            let tiledImage = ti;
            if (typeof ti === "number") {
                tiledImage = world.getItemAt(ti);
            }
            if (!tiledImage) {
                return 4;
            }

            return tiledImage.__flexComponentsPerPack || 4;
        }

        getChannelCount(ti) {
            const world = this.viewer.world;
            if (!world) {
                return 4;
            }

            let tiledImage = ti;
            if (typeof ti === "number") {
                tiledImage = world.getItemAt(ti);
            }
            if (!tiledImage) {
                return 4;
            }

            // fall back to packCount * componentsPerPack, preserving old semantics for RGBA
            if (typeof tiledImage.__flexChannelCount === "number") {
                return tiledImage.__flexChannelCount;
            }
            const pc = tiledImage.__flexPackCount || 1;
            return pc * (tiledImage.__flexComponentsPerPack || 4);
        }

        _hasInvalidBuildState() {
            return this._requestBuildStamp > this._buildStamp;
        }

        _requestRebuild(timeout = 30, force = false, bypassSuspend = false, immediate = false) {
            this._requestBuildStamp = Date.now();
            this._drawReady = false;

            if (!bypassSuspend && this._isRenderingSuspended()) {
                const pending = this._pendingRebuildRequest || { timeout, force };
                pending.timeout = Math.min(pending.timeout, timeout);
                pending.force = pending.force || force;
                this._pendingRebuildRequest = pending;
                return $.Promise.resolve();
            }

            if (this._rebuildHandle) {
                if (!force && !immediate) {
                    return $.Promise.resolve();
                }
                clearTimeout(this._rebuildHandle);
                this._rebuildHandle = null;
            }

            const runRebuild = () => {
                if (this._isRenderingSuspended()) {
                    this._pendingRebuildRequest = { timeout: 0, force: true };
                    this._rebuildHandle = null;
                    this._drawReady = false;
                    return;
                }

                if (this._destroyed) {
                    this._rebuildHandle = null;
                    return;
                }

                if (!this._configuredExternally) {
                    // __shaderConfig may be missing for an item mid-teardown during a reset window
                    // (remove-item deletes it, then this rebuild fires deferred) — skip such items
                    const derived = this.viewer.world._items
                        .filter(item => item.__shaderConfig)
                        .map(item => item.__shaderConfig.id);

                    // With one world item that reset window is all-or-nothing: the single item
                    // losing __shaderConfig empties the whole list, so the second pass compiled an
                    // empty stack and the viewer went white. With two or more items a survivor kept
                    // it non-empty, which is why the failure looked like it depended on the world
                    // size. Keep the previous order across the window instead of erasing it.
                    if (derived.length || !this.viewer.world.getItemCount()) {
                        this.renderer.setShaderLayerOrder(derived);
                    } else {
                        $.console.warn(`[flex-renderer] rebuild kept the previous render order: ` +
                            `${this.viewer.world.getItemCount()} world item(s) but none carry ` +
                            `__shaderConfig yet (reset window).`);
                    }
                }

                this._buildStamp = Date.now();
                this._setOffscreenDimensions(
                    this.canvas.width,
                    this.canvas.height,
                    this._computeOffscreenLayerCount(),
                    this.viewer.world.getItemCount()
                );
                this._updatePackLayout();
                try {
                    this.renderer.registerProgram(null, this.renderer.backend.secondPassProgramKey);
                } catch (e) {
                    // The previously linked program is untouched by a failed registerProgram()
                    // and still matches the shader set that produced it, so it keeps rendering
                    // the last good frame. This used to call overrideConfigureAll(undefined),
                    // which deleted every shader -- and since the drawer never retained the
                    // externally supplied `shaders` map, the configuration was destroyed rather
                    // than disabled: every later rebuild rendered identity for the rest of the
                    // page life, with no way back short of a reload.
                    $.console.error("[flex-renderer] second-pass program build failed; the " +
                        "previous program is kept and the configuration is retained.", e);
                    this.renderer.notifyProgramBuildFailed(
                        this.renderer.backend.secondPassProgramKey, e, "drawer-rebuild");
                } finally {
                    // The handle must be cleared no matter the outcome, otherwise every later
                    // _requestRebuild() believes a rebuild is already pending and schedules nothing.
                    this.rebuildCounter++;
                    this._rebuildHandle = null;
                    this._refreshDrawReadyState();
                }

                this._warnOnMissingInteractionForwarding();

                if (!immediate) {
                    this._deferredRedrawHandle = setTimeout(() => {
                        this._deferredRedrawHandle = null;
                        if (this._destroyed) {
                            return;
                        }
                        if (!this._isRenderingSuspended()) {
                            try {
                                this.viewer.forceRedraw();
                            } catch (_) {
                                // viewer destroyed between schedule and fire — OSD's private
                                // state slot is gone; post-teardown redraw is a no-op.
                            }
                        }
                    });
                }
            };

            if (immediate) {
                runRebuild();
            } else {
                this._rebuildHandle = setTimeout(runRebuild, timeout);
            }

            return $.Promise.resolve();
        }

        /**
         * Initial setup of all three canvases used (output, rendering) and their contexts (2d, 2d, webgl)
         */
        _setupCanvases() {
            // this._outputCanvas = this.canvas; //canvas on screen
            // this._outputContext = this._outputCanvas.getContext('2d');

            // this._renderingCanvas = this.renderer.canvas; //canvas for webgl

            // this._renderingCanvas.width = this._outputCanvas.width;
            // this._renderingCanvas.height = this._outputCanvas.height;

            this._resizeHandler = () => {
                // if(this._outputCanvas !== this.viewer.drawer.canvas) {
                //     this._outputCanvas.style.width = this.viewer.drawer.canvas.clientWidth + 'px';
                //     this._outputCanvas.style.height = this.viewer.drawer.canvas.clientHeight + 'px';
                // }

                let viewportSize = this._calculateCanvasSize();
                if (this.debug) {
                    console.info('Resize event, newWidth, newHeight:', viewportSize.x, viewportSize.y);
                }

                // if( this._outputCanvas.width !== viewportSize.x ||
                //     this._outputCanvas.height !== viewportSize.y ) {
                //     this._outputCanvas.width = viewportSize.x;
                //     this._outputCanvas.height = viewportSize.y;
                // }

                // todo necessary?
                // this._renderingCanvas.style.width = this._outputCanvas.clientWidth + 'px';
                // this._renderingCanvas.style.height = this._outputCanvas.clientHeight + 'px';
                // this._renderingCanvas.width = this._outputCanvas.width;
                // this._renderingCanvas.height = this._outputCanvas.height;

                //todo batched?
                this._setOffscreenDimensions(viewportSize.x, viewportSize.y, this._computeOffscreenLayerCount(), this.viewer.world.getItemCount());
                this._size = viewportSize;
                this._refreshDrawReadyState();
            };

            this.viewer.addHandler("resize", this._resizeHandler);
        }

        _resolveRenderView(view = undefined) {
            if (view) {
                return view;
            }

            const bounds = this.viewport.getBoundsNoRotateWithMargins(true);
            return {
                bounds: bounds,
                center: new OpenSeadragon.Point(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2),
                rotation: this.viewport.getRotation(true) * Math.PI / 180,
                zoom: this.viewport.getZoom(true)
            };
        }

        /**
         * Build the current second-pass uniform payload for a set of shaders.
         * The returned array is backend-neutral input for `renderer.renderSecondPass(...)`
         * and `renderer.renderSecondPassToTexture(...)`.
         * @param {Object} [view=undefined]
         * @param {Object.<string, ShaderLayer>} [shaderMap=this.renderer.getAllShaders()]
         * @param {string[]} [shaderOrder=this.renderer.getShaderLayerOrder()]
         * @return {SPRenderPackage[]}
         */
        getCurrentShaderRenderArray(view = undefined, shaderMap = undefined, shaderOrder = undefined) {
            view = this._resolveRenderView(view);
            shaderMap = shaderMap || this.renderer.getAllShaders();
            shaderOrder = shaderOrder || this.renderer.getShaderLayerOrder();
            return this._collectShaderUniforms(shaderMap, shaderOrder, view);
        }

        /**
         * Render the current visualization into an offscreen target using the active backend's
         * `renderSecondPassToTexture(...)` implementation.
         *
         * This is the public drawer-level convenience wrapper for callers that want a texture
         * result but do not want to assemble the second-pass render array themselves.
         *
         * @param {Object} [options]
         * @return {Object}
         */
        renderVisualizationToTexture(options = {}) {
            const view = this._resolveRenderView(options.view);
            const shaderMap = options.shaderMap || this.renderer.getAllShaders();
            const shaderOrder = options.shaderOrder || this.renderer.getShaderLayerOrder();
            const renderArray = this._collectShaderUniforms(shaderMap, shaderOrder, view);
            return this.renderer.renderSecondPassToTexture(renderArray, options);
        }

        /**
         * Drawer-level convenience API for updating the renderer-owned inspector state.
         *
         * The drawer does not implement inspector rendering itself and does not synchronize
         * inspector state to the navigator. Backend implementations must consume the state
         * through `renderer.getInspectorState()`.
         *
         * @param {Partial<InspectorState>|undefined} state
         * @return {InspectorState}
         */
        setInspectorState(state) {
            return this.renderer.setInspectorState(state, {
                reason: "drawer-set-inspector-state"
            });
        }

        /**
         * Reset inspector state through the renderer-owned API.
         *
         * @return {InspectorState}
         */
        clearInspectorState() {
            return this.setInspectorState(undefined);
        }

        /**
         * Drawer-level interaction observer configuration.
         *
         * This controls whether `FlexDrawer` observes pointer/mouse events and how it
         * forwards those events to the renderer-owned interaction state. These options
         * are drawer configuration options, not `FlexRenderer` interaction-state update
         * options.
         *
         * @typedef {Object} FlexDrawerInteractionOptions
         * @property {boolean} [enabled=false] - Whether FlexDrawer observes pointer/mouse events and forwards interaction state.
         * @property {boolean} [preventContextMenu=false] - Prevent the browser context menu on interaction right-click/contextmenu events.
         * @property {boolean} [notifyOnMove=false] - Emit `interaction-change` notifications for high-frequency pointermove updates.
         * @property {"all"|"drag"|"none"} [viewerInputCaptureMode="none"] - Viewer input suppression mode. `"none"` leaves OpenSeadragon viewer input unchanged. `"all"` disables OpenSeadragon mouse navigation. `"drag"` disables drag/click/flick gestures but leaves wheel zoom enabled.
         * @property {HTMLElement|string|function} [eventTarget] - Element (or CSS selector, or `drawer => element` factory) to bind pointer listeners to. Defaults to the viewer container. Use it when the host's overlay stack lives outside that subtree.
         */

        /**
         * Normalize viewer input capture mode.
         *
         * @private
         * @param {*} mode
         * @return {"all"|"drag"|"none"}
         */
        _normalizeViewerInputCaptureMode(mode) {
            return mode === "all" || mode === "drag" ? mode : "none";
        }

        /**
         * Normalize drawer-level interaction configuration.
         *
         * @private
         * @param {boolean|Partial<FlexDrawerInteractionOptions>|undefined} interaction
         * @return {FlexDrawerInteractionOptions}
         */
        _normalizeInteractionOptions(interaction = false) {
            if (interaction === true) {
                return {
                    enabled: true,
                    preventContextMenu: false,
                    notifyOnMove: false,
                    viewerInputCaptureMode: "none",
                    eventTarget: null,
                };
            }

            if (!interaction || typeof interaction !== "object") {
                return {
                    enabled: false,
                    preventContextMenu: false,
                    notifyOnMove: false,
                    viewerInputCaptureMode: "none",
                    eventTarget: null,
                };
            }

            const viewerInputCaptureMode = this._normalizeViewerInputCaptureMode(
                interaction.viewerInputCaptureMode
            );

            return {
                enabled: !!interaction.enabled,
                preventContextMenu: !!interaction.preventContextMenu,
                notifyOnMove: !!interaction.notifyOnMove,
                viewerInputCaptureMode: viewerInputCaptureMode,
                // This is a whitelist: an unlisted key never survives into _interactionOptions.
                eventTarget: interaction.eventTarget || null,
            };
        }

        /**
         * Shader types already reported by `_warnOnMissingInteractionForwarding()`.
         *
         * Lazily created: the drawer's own interaction setup runs from the base-class
         * constructor (through `_createDrawingElement()`), before the subclass constructor
         * body assigns its fields.
         *
         * @private
         * @return {Set<string>}
         */
        _interactionWarnedTypes() {
            if (!this._interactionForwardingWarnedTypes) {
                this._interactionForwardingWarnedTypes = new Set();
            }
            return this._interactionForwardingWarnedTypes;
        }

        /**
         * Warn once per shader type when a layer declaring `static requiresInteraction()`
         * is built while interaction forwarding is off.
         *
         * Such a layer compiles and draws either way — it just renders its inactive branch
         * (no lens, transparent overlay), which is indistinguishable from a broken shader.
         * The drawer never enables forwarding on its own: every changed pointer move costs a
         * `viewer.forceRedraw()`, so the decision stays with the host.
         *
         * Note this is about pointer state reaching the GLSL, not about a UI control's
         * `interactive` flag.
         *
         * @private
         */
        _warnOnMissingInteractionForwarding() {
            if (this._destroyed || this._interactionOptions.enabled || !this.renderer ||
                typeof this.renderer.getFlatShaderLayers !== "function") {
                return;
            }

            const warned = this._interactionWarnedTypes();

            // getFlatShaderLayers() also descends into group children, which carry their own
            // GLSL and can be the only consumer of interaction state in the program
            for (const layer of this.renderer.getFlatShaderLayers()) {
                const Klass = layer && layer.constructor;
                if (!Klass || typeof Klass.requiresInteraction !== "function" ||
                    Klass.requiresInteraction() !== true) {
                    continue;
                }

                const type = typeof Klass.type === "function" ? Klass.type() : "unknown";
                if (warned.has(type)) {
                    continue;
                }
                warned.add(type);

                $.console.warn(`[flex-renderer] ShaderLayer '${type}' declares ` +
                    "static requiresInteraction() === true, but FlexDrawer interaction forwarding " +
                    "is disabled, so it renders without pointer state. Enable it with " +
                    "drawer.setInteractionEnabled(true) (or the drawer option " +
                    "interaction: {enabled: true}).");
            }
        }

        /**
         * Warn when the element the interaction listeners are bound to is covered by another
         * element, so no pointer event can reach it.
         *
         * This is the one failure mode of drawer-side forwarding that is otherwise invisible:
         * `isInteractionEnabled()` reads back `true`, listeners are attached, and yet nothing
         * ever fires because a host overlay outside the target's subtree sits on top. The
         * uniforms then hold their zero defaults, which every interaction-driven layer renders
         * as "pointer never entered".
         *
         * Deferred one frame so the check runs against settled layout, and skipped entirely on a
         * zero-sized rect (viewer not laid out yet) where a hit test carries no information.
         *
         * @private
         * @return {void}
         */
        _warnOnCoveredInteractionTarget() {
            const target = this._interactionListeners && this._interactionListeners.target;
            if (!target || typeof target.getBoundingClientRect !== "function" ||
                    typeof document === "undefined" ||
                    typeof document.elementFromPoint !== "function") {
                return;
            }

            const check = () => {
                if (this._destroyed || !this._interactionEnabled || !this._interactionListeners ||
                        this._interactionListeners.target !== target) {
                    return;
                }

                const rect = target.getBoundingClientRect();
                if (rect.width < 1 || rect.height < 1) {
                    return;
                }

                const top = document.elementFromPoint(
                    rect.left + rect.width / 2,
                    rect.top + rect.height / 2
                );

                // A descendant on top is fine: pointer events bubble up to the target.
                if (!top || top === target || target.contains(top)) {
                    return;
                }

                $.console.warn("[flex-renderer] FlexDrawer interaction forwarding is enabled, but",
                    top, "covers the event target", target,
                    "- pointer events will not reach it and the interaction uniforms stay at " +
                    "their defaults. Bind above the overlay with interaction.eventTarget, or " +
                    "drive renderer.setInteractionState(...) from the host's own input handling.");
            };

            if (typeof requestAnimationFrame === "function") {
                requestAnimationFrame(check);
            } else {
                check();
            }
        }

        /**
         * Return the DOM element used for interaction event observation.
         *
         * Defaults to `this.container` — OpenSeadragon's `viewer.canvas` div, the element the
         * viewer's own MouseTracker binds. The drawer's own canvas is deliberately not the
         * default: any host that stacks an overlay above the drawer (an annotation canvas, a
         * fabric.js `upper-canvas`) takes every pointer event, so listeners on the WebGL canvas
         * never fire and the interaction uniforms silently stay at their zero defaults. The
         * container is an ancestor of both the drawer canvas and of overlays added through the
         * OpenSeadragon overlay mechanism, so events reach it by bubbling.
         *
         * Hosts whose overlay lives outside that subtree can name their own element through
         * `interaction.eventTarget`.
         *
         * @private
         * @return {HTMLElement|HTMLCanvasElement|null}
         */
        _getInteractionEventTarget() {
            const explicit = this._interactionOptions && this._interactionOptions.eventTarget;
            let resolved = null;
            if (typeof explicit === "string") {
                resolved = document.querySelector(explicit);
                if (!resolved) {
                    $.console.warn("FlexDrawer: interaction.eventTarget selector", explicit,
                        "matched no element; falling back to the viewer container.");
                }
            } else if (typeof explicit === "function") {
                resolved = explicit(this);
            } else if (explicit) {
                resolved = explicit;
            }

            return resolved || this.container || this.canvas || this.element ||
                (this.viewer && this.viewer.element) || null;
        }

        /**
         * Return the element whose client rect defines framebuffer coordinate space.
         *
         * This is the presented canvas, not the event target: the two are the same element only
         * when interaction listens on the drawer canvas. With the default container target, using
         * the listening element's rect would offset every reported pointer position by the
         * container/canvas inset.
         *
         * @private
         * @return {HTMLElement|HTMLCanvasElement|null}
         */
        _getInteractionCoordinateElement() {
            const presented = this.renderer && this.renderer.getPresentationCanvas();
            if (presented && typeof presented.getBoundingClientRect === "function" &&
                    presented.isConnected !== false) {
                return presented;
            }
            return this.canvas || this._getInteractionEventTarget();
        }

        /**
         * Convert a client-space point ({clientX, clientY} from a DOM event,
         * or any object with those fields) into renderer framebuffer pixels.
         *
         * Returned coordinates use physical framebuffer pixels with bottom-left
         * origin, directly comparable to `gl_FragCoord.xy`. The conversion is
         * devicePixelRatio-aware because `canvas.width / rect.width` already
         * folds DPR into the scale factor.
         *
         * Intended for application code that drives `inspector.centerPx` from
         * a pointer event. Returns `{x:0, y:0}` when no canvas or event target
         * is available.
         *
         * @param {{clientX: number, clientY: number}} point
         * @return {{x: number, y: number}}
         */
        clientPointToFramebufferPx(point) {
            const canvas = this.renderer && this.renderer.getPresentationCanvas();
            // The rect must come from the presented canvas, not from whatever element the
            // listeners happen to be bound to — see _getInteractionCoordinateElement().
            const target = this._getInteractionCoordinateElement();

            if (!canvas || !target || typeof target.getBoundingClientRect !== "function") {
                return { x: 0, y: 0 };
            }

            const rect = target.getBoundingClientRect();
            const scaleX = rect.width ? canvas.width / rect.width : 1;
            const scaleY = rect.height ? canvas.height / rect.height : 1;

            return {
                x: (point.clientX - rect.left) * scaleX,
                y: (rect.bottom - point.clientY) * scaleY,
            };
        }

        /**
         * Convert a DOM pointer/mouse event into renderer framebuffer pixels.
         *
         * @private
         * @param {PointerEvent|MouseEvent} event
         * @return {{x: number, y: number}}
         */
        _getInteractionPositionPx(event) {
            return this.clientPointToFramebufferPx(event);
        }

        /**
         * Convert a MouseEvent.button value into a MouseEvent.buttons-compatible bitmask.
         *
         * @private
         * @param {number} button
         * @return {number}
         */
        _buttonToButtonsMask(button) {
            if (button === 0) {
                return 1;
            }
            if (button === 1) {
                return 4;
            }
            if (button === 2) {
                return 2;
            }
            if (button === 3) {
                return 8;
            }
            if (button === 4) {
                return 16;
            }
            return 0;
        }

        /**
         * Return the current button bitmask from an interaction event.
         *
         * @private
         * @param {PointerEvent|MouseEvent} event
         * @return {number}
         */
        _getInteractionButtons(event) {
            if (typeof event.buttons === "number") {
                return event.buttons;
            }

            return this._buttonToButtonsMask(event.button);
        }

        /**
         * Return whether this event should be ignored by the initial mouse-focused implementation.
         *
         * @private
         * @param {PointerEvent|MouseEvent} event
         * @return {boolean}
         */
        _shouldIgnoreInteractionEvent(event) {
            if (!this._interactionEnabled) {
                return true;
            }

            return !!(event.pointerType && event.pointerType !== "mouse");
        }

        /**
         * Reset drawer-local interaction tracking fields.
         *
         * @private
         * @return {void}
         */
        _resetInteractionTracking() {
            this._interactionDragActive = false;
        }

        /**
         * Attach pointer or mouse observers used to forward interaction state to FlexRenderer.
         *
         * @private
         * @return {void}
         */
        _attachInteractionListeners() {
            if (this._interactionListeners) {
                return;
            }

            const target = this._getInteractionEventTarget();
            if (!target) {
                return;
            }

            const supportsPointerEvents = typeof window !== "undefined" && !!window.PointerEvent;
            const listeners = [];

            const add = (type, handler) => {
                target.addEventListener(type, handler, false);
                listeners.push({ type, handler });
            };

            const handleEnter = (event) => {
                if (this._shouldIgnoreInteractionEvent(event)) {
                    return;
                }

                this.setInteractionState({
                    enabled: true,
                    pointerInside: true,
                    pointerPositionPx: this._getInteractionPositionPx(event),
                    activeButtons: this._getInteractionButtons(event),
                }, {
                    notify: true,
                    reason: "drawer-pointerenter"
                });
            };

            const handleMove = (event) => {
                if (this._shouldIgnoreInteractionEvent(event)) {
                    return;
                }

                const pointerPositionPx = this._getInteractionPositionPx(event);
                const patch = {
                    enabled: true,
                    pointerInside: true,
                    pointerPositionPx: pointerPositionPx,
                    activeButtons: this._getInteractionButtons(event),
                };

                if (this._interactionDragActive) {
                    patch.dragCurrentPositionPx = pointerPositionPx;
                }

                this.setInteractionState(patch, {
                    notify: this._interactionOptions.notifyOnMove,
                    reason: "drawer-pointermove"
                });
            };

            const handleDown = (event) => {
                if (this._shouldIgnoreInteractionEvent(event)) {
                    return;
                }

                const pointerPositionPx = this._getInteractionPositionPx(event);
                const activeButtons = this._getInteractionButtons(event);

                this._interactionDragActive = true;

                this.setInteractionState({
                    enabled: true,
                    pointerInside: true,
                    pointerPositionPx: pointerPositionPx,
                    activeButtons: activeButtons,
                    dragActive: true,
                    dragStartPositionPx: pointerPositionPx,
                    dragCurrentPositionPx: pointerPositionPx,
                    dragButtons: activeButtons,
                }, {
                    notify: true,
                    reason: "drawer-pointerdown"
                });
            };

            const handleUp = (event) => {
                if (this._shouldIgnoreInteractionEvent(event)) {
                    return;
                }

                const previous = this.getInteractionState();
                const pointerPositionPx = this._getInteractionPositionPx(event);
                const activeButtons = this._getInteractionButtons(event);
                const completedDrag = this._interactionDragActive || previous.dragActive;

                this._resetInteractionTracking();

                this.setInteractionState({
                    enabled: true,
                    pointerInside: true,
                    pointerPositionPx: pointerPositionPx,
                    activeButtons: activeButtons,
                    dragActive: false,
                    dragCurrentPositionPx: pointerPositionPx,
                    dragEndPositionPx: pointerPositionPx,
                    dragSerial: completedDrag ? previous.dragSerial + 1 : previous.dragSerial,
                }, {
                    notify: true,
                    reason: completedDrag ? "drawer-drag-end" : "drawer-pointerup"
                });
            };

            const handleLeave = (event) => {
                if (this._shouldIgnoreInteractionEvent(event)) {
                    return;
                }

                this._resetInteractionTracking();

                this.setInteractionState({
                    pointerInside: false,
                    activeButtons: 0,
                    dragActive: false,
                }, {
                    notify: true,
                    reason: "drawer-pointerleave"
                });
            };

            const handleCancel = (event) => {
                if (this._shouldIgnoreInteractionEvent(event)) {
                    return;
                }

                this._resetInteractionTracking();

                this.setInteractionState({
                    pointerInside: false,
                    activeButtons: 0,
                    dragActive: false,
                }, {
                    notify: true,
                    reason: "drawer-pointercancel"
                });
            };

            const handleClick = (event) => {
                if (this._shouldIgnoreInteractionEvent(event)) {
                    return;
                }

                const previous = this.getInteractionState();
                const pointerPositionPx = this._getInteractionPositionPx(event);

                this.setInteractionState({
                    enabled: true,
                    pointerInside: true,
                    pointerPositionPx: pointerPositionPx,
                    lastClickPositionPx: pointerPositionPx,
                    lastClickButtons: this._buttonToButtonsMask(event.button),
                    clickSerial: previous.clickSerial + 1,
                }, {
                    notify: true,
                    reason: "drawer-click"
                });
            };

            const handleContextMenu = (event) => {
                if (this._interactionEnabled && this._interactionOptions.preventContextMenu) {
                    event.preventDefault();
                }
            };

            if (supportsPointerEvents) {
                add("pointerenter", handleEnter);
                add("pointermove", handleMove);
                add("pointerdown", handleDown);
                add("pointerup", handleUp);
                add("pointercancel", handleCancel);
                add("pointerleave", handleLeave);
                add("click", handleClick);
            } else {
                add("mouseenter", handleEnter);
                add("mousemove", handleMove);
                add("mousedown", handleDown);
                add("mouseup", handleUp);
                add("mouseleave", handleLeave);
                add("click", handleClick);
            }

            add("contextmenu", handleContextMenu);

            this._interactionListeners = {
                target,
                listeners
            };
        }

        /**
         * Detach pointer or mouse observers used for interaction forwarding.
         *
         * @private
         * @return {void}
         */
        _detachInteractionListeners() {
            if (!this._interactionListeners) {
                return;
            }

            const target = this._interactionListeners.target;
            for (const listener of this._interactionListeners.listeners) {
                target.removeEventListener(listener.type, listener.handler, false);
            }

            this._interactionListeners = null;
        }

        /**
         * Return whether OpenSeadragon mouse navigation is currently enabled.
         *
         * @private
         * @return {boolean}
         */
        _getViewerMouseNavEnabled() {
            if (!this.viewer) {
                return true;
            }

            if (
                this.viewer.innerTracker &&
                typeof this.viewer.innerTracker.isTracking === "function"
            ) {
                const tracking = this.viewer.innerTracker.isTracking();

                if (typeof tracking === "boolean") {
                    return tracking;
                }
            }

            if (typeof this.viewer.isMouseNavEnabled === "function") {
                const enabled = this.viewer.isMouseNavEnabled();

                if (typeof enabled === "boolean") {
                    return enabled;
                }
            }

            if (typeof this.viewer.mouseNavEnabled === "boolean") {
                return this.viewer.mouseNavEnabled;
            }

            return true;
        }

        /**
         * Disable OpenSeadragon mouse navigation while interaction forwarding is active.
         *
         * This is the `"all"` capture mode. It disables the OpenSeadragon mouse
         * tracker as a whole and restores its previous tracking state on release.
         *
         * @private
         * @return {void}
         */
        _captureInteractionMouseNavigation() {
            if (this._interactionMouseNavCaptured || !this.viewer) {
                return;
            }

            // Mark as captured before mutating OpenSeadragon state. This prevents a
            // synchronous re-entrant capture path from overwriting the saved previous
            // value after setMouseNavEnabled(false) has already disabled navigation.
            this._interactionPreviousMouseNavEnabled = this._getViewerMouseNavEnabled();
            this._interactionMouseNavCaptured = true;

            if (typeof this.viewer.setMouseNavEnabled === "function") {
                this.viewer.setMouseNavEnabled(false);
                return;
            }

            if (typeof this.viewer.mouseNavEnabled === "boolean") {
                this.viewer.mouseNavEnabled = false;
                return;
            }

            this._interactionMouseNavCaptured = false;
            this._interactionPreviousMouseNavEnabled = null;
        }

        /**
         * Restore OpenSeadragon mouse navigation after `"all"` input capture.
         *
         * @private
         * @param {boolean} [force=false] - Attempt restoration even if the local capture flag is stale.
         * @param {boolean|null} [forceEnabled=null] - Explicit restored mouse-navigation state.
         * @return {void}
         */
        _releaseInteractionMouseNavigationCapture(force = false, forceEnabled = null) {
            if ((!this._interactionMouseNavCaptured && !force) || !this.viewer) {
                this._interactionMouseNavCaptured = false;
                this._interactionPreviousMouseNavEnabled = null;
                return;
            }

            const restoreEnabled = typeof forceEnabled === "boolean" ?
                forceEnabled :
                this._interactionPreviousMouseNavEnabled !== false;

            this._interactionMouseNavCaptured = false;
            this._interactionPreviousMouseNavEnabled = null;

            if (typeof this.viewer.setMouseNavEnabled === "function") {
                this.viewer.setMouseNavEnabled(restoreEnabled);
            } else if (typeof this.viewer.mouseNavEnabled === "boolean") {
                this.viewer.mouseNavEnabled = restoreEnabled;
            }
        }

        /**
         * Disable drag/click OpenSeadragon gestures while keeping wheel zoom available.
         *
         * This is the `"drag"` capture mode. It keeps the OpenSeadragon mouse tracker
         * active, but temporarily disables mouse gesture settings that would conflict
         * with shader interaction tools.
         *
         * @private
         * @return {void}
         */
        _captureInteractionGestureSettings() {
            if (this._interactionGestureSettingsCaptured || !this.viewer) {
                return;
            }

            const settings = this.viewer.gestureSettingsMouse;

            if (!settings || typeof settings !== "object") {
                return;
            }

            this._interactionPreviousGestureSettings = {
                dragToPan: settings.dragToPan,
                clickToZoom: settings.clickToZoom,
                dblClickToZoom: settings.dblClickToZoom,
                dblClickDragToZoom: settings.dblClickDragToZoom,
                flickEnabled: settings.flickEnabled,
            };
            this._interactionGestureSettingsCaptured = true;

            settings.dragToPan = false;
            settings.clickToZoom = false;
            settings.dblClickToZoom = false;

            if ("dblClickDragToZoom" in settings) {
                settings.dblClickDragToZoom = false;
            }

            if ("flickEnabled" in settings) {
                settings.flickEnabled = false;
            }
        }

        /**
         * Restore OpenSeadragon gesture settings after `"drag"` input capture.
         *
         * @private
         * @param {boolean} [force=false] - Attempt restoration even if the local capture flag is stale.
         * @param {Object|null} [forceSettings=null] - Explicit gesture settings to restore.
         * @return {void}
         */
        _releaseInteractionGestureSettingsCapture(force = false, forceSettings = null) {
            if ((!this._interactionGestureSettingsCaptured && !force) || !this.viewer) {
                this._interactionGestureSettingsCaptured = false;
                this._interactionPreviousGestureSettings = null;
                return;
            }

            const settings = this.viewer.gestureSettingsMouse;
            const previous = forceSettings || this._interactionPreviousGestureSettings || {};

            this._interactionGestureSettingsCaptured = false;
            this._interactionPreviousGestureSettings = null;

            if (!settings || typeof settings !== "object") {
                return;
            }

            for (const key of Object.keys(previous)) {
                if (previous[key] !== undefined) {
                    settings[key] = previous[key];
                }
            }
        }

        /**
         * Suppress OpenSeadragon viewer input according to current interaction options.
         *
         * @private
         * @return {void}
         */
        _captureInteractionViewerInput() {
            if (!this._interactionOptions) {
                this._restoreInteractionViewerInputDefaults();
                return;
            }

            const mode = this._interactionOptions.viewerInputCaptureMode || "none";

            if (mode === "drag") {
                this._releaseInteractionMouseNavigationCapture();
                this._captureInteractionGestureSettings();
                return;
            }

            if (mode === "all") {
                this._releaseInteractionGestureSettingsCapture();
                this._captureInteractionMouseNavigation();
                return;
            }

            this._restoreInteractionViewerInputDefaults();
        }

        /**
         * Restore all OpenSeadragon viewer input modified by interaction capture.
         *
         * @private
         * @return {void}
         */
        _releaseInteractionViewerInputCapture() {
            this._releaseInteractionMouseNavigationCapture();
            this._releaseInteractionGestureSettingsCapture();
        }

        /**
         * Restore normal OpenSeadragon mouse input after viewer input capture is
         * explicitly set to `"none"`.
         *
         * This is intentionally stronger than restoring only the saved capture
         * snapshot. It prevents stale or already-mutated gesture snapshots from
         * leaving the viewer unable to pan or click-zoom after capture is turned off.
         *
         * @private
         * @return {void}
         */
        _restoreInteractionViewerInputDefaults() {
            this._releaseInteractionMouseNavigationCapture(true, true);

            this._releaseInteractionGestureSettingsCapture(true, {
                dragToPan: true,
                clickToZoom: true,
                dblClickToZoom: true,
                dblClickDragToZoom: true,
                flickEnabled: true,
            });
        }

        /**
         * Synchronize OpenSeadragon input capture with current drawer interaction options.
         *
         * @private
         * @return {void}
         */
        _syncInteractionViewerInputCapture() {
            if (
                this._interactionEnabled &&
                this._interactionOptions
            ) {
                this._captureInteractionViewerInput();
                return;
            }

            this._releaseInteractionViewerInputCapture();
        }

        /**
         * Update drawer-level interaction observer options.
         *
         * This is the main implementation for drawer-side interaction configuration.
         * It controls whether FlexDrawer observes pointer/mouse events and how those
         * events are forwarded to the renderer-owned interaction state.
         *
         * The second argument is forwarded only when this method needs to mutate
         * renderer-owned interaction state because `enabled` changed.
         *
         * @param {boolean|Partial<FlexDrawerInteractionOptions>} interaction
         * @param {InteractionStateUpdateOptions} [stateOptions={}] - Renderer state update options used only when enabling/disabling forwarding.
         * @return {FlexDrawerInteractionOptions}
         */
        setInteractionOptions(interaction, stateOptions = {}) {
            const previousEnabled = this._interactionEnabled;
            const previousOptions = this.getInteractionOptions();
            const previousViewerInputCaptureMode = previousOptions.viewerInputCaptureMode || "none";

            const nextOptions = this._normalizeInteractionOptions(
                interaction && typeof interaction === "object" ?
                    $.extend(true, {}, this._interactionOptions, interaction) :
                    interaction
            );

            this._interactionOptions = nextOptions;

            if (nextOptions.enabled) {
                // A changed eventTarget must move the listeners: _attachInteractionListeners()
                // is a no-op while a previous binding exists.
                if (this._interactionListeners &&
                        this._interactionListeners.target !== this._getInteractionEventTarget()) {
                    this._detachInteractionListeners();
                }

                if (!this._interactionListeners) {
                    this._attachInteractionListeners();
                    this._warnOnCoveredInteractionTarget();
                }

                if (!this._interactionListeners) {
                    this._interactionEnabled = false;
                    this._interactionOptions.enabled = false;
                    return this.getInteractionOptions();
                }

                this._interactionEnabled = true;
                this._interactionOptions.enabled = true;
                // forwarding is on: a later disable is a new situation and warns again.
                // Guarded: the base constructor reaches this through _createDrawingElement(),
                // before the subclass constructor body has run.
                this._interactionWarnedTypes().clear();

                if (
                    previousViewerInputCaptureMode !== "none" &&
                    this._interactionOptions.viewerInputCaptureMode === "none"
                ) {
                    this._restoreInteractionViewerInputDefaults();
                } else {
                    this._syncInteractionViewerInputCapture();
                }

                if (!previousEnabled) {
                    this.setInteractionState({
                        enabled: true
                    }, $.extend(true, {
                        reason: "drawer-enable-interaction"
                    }, stateOptions));
                }

                return this.getInteractionOptions();
            }

            this._interactionEnabled = false;
            this._interactionOptions.enabled = false;
            this._detachInteractionListeners();
            this._resetInteractionTracking();
            this._releaseInteractionViewerInputCapture();

            this.clearInteractionState($.extend(true, {
                reason: "drawer-disable-interaction"
            }, stateOptions));

            return this.getInteractionOptions();
        }

        /**
         * Return drawer-level interaction observer options.
         *
         * @return {FlexDrawerInteractionOptions}
         */
        getInteractionOptions() {
            return $.extend(true, {}, this._interactionOptions);
        }

        /**
         * Enable or disable drawer-side interaction observation and forwarding.
         *
         * On FlexDrawer, "interaction enabled" means the drawer observes pointer/mouse
         * events and forwards normalized interaction state to FlexRenderer.
         *
         * This is a convenience wrapper around `setInteractionOptions(...)`.
         *
         * @param {boolean} enabled
         * @param {InteractionStateUpdateOptions} [stateOptions={}] - Renderer state update options used only for the enable/disable state mutation.
         * @return {FlexDrawerInteractionOptions}
         */
        setInteractionEnabled(enabled, stateOptions = {}) {
            return this.setInteractionOptions({
                enabled: !!enabled
            }, stateOptions);
        }

        /**
         * Return whether drawer-side interaction observation and forwarding is enabled.
         *
         * @return {boolean}
         */
        isInteractionEnabled() {
            return !!this.getInteractionOptions().enabled;
        }

        /**
         * Forward an interaction-state patch to the renderer-owned interaction API.
         *
         * @param {Partial<InteractionState>|undefined} state
         * @param {InteractionStateUpdateOptions} [options={}]
         * @return {InteractionState}
         */
        setInteractionState(state = undefined, options = {}) {
            if (!this.renderer || typeof this.renderer.setInteractionState !== "function") {
                return OpenSeadragon.FlexRenderer.normalizeInteractionState();
            }

            return this.renderer.setInteractionState(state, $.extend(true, {
                reason: "drawer-set-interaction-state"
            }, options));
        }

        /**
         * Return the renderer-owned canonical interaction state.
         *
         * @return {InteractionState}
         */
        getInteractionState() {
            if (!this.renderer || typeof this.renderer.getInteractionState !== "function") {
                return OpenSeadragon.FlexRenderer.normalizeInteractionState();
            }

            return this.renderer.getInteractionState();
        }

        /**
         * Clear drawer-local interaction tracking and renderer-owned interaction state.
         *
         * @param {InteractionStateUpdateOptions} [options={}]
         * @return {InteractionState}
         */
        clearInteractionState(options = {}) {
            this._resetInteractionTracking();

            if (!this.renderer || typeof this.renderer.clearInteractionState !== "function") {
                return OpenSeadragon.FlexRenderer.normalizeInteractionState();
            }

            return this.renderer.clearInteractionState($.extend(true, {
                reason: "drawer-clear-interaction-state"
            }, options));
        }

        // DRAWING METHODS
        /**
         * Draw using `FlexRenderer`.
         *
         * This method is the OpenSeadragon `DrawerBase` draw entry point. It remains
         * responsible for OpenSeadragon-specific adaptation: resolving the current
         * viewport, building the viewport matrix, collecting tile data, and collecting
         * ShaderLayer render uniforms that depend on TiledImage state.
         *
         * The actual two-pass render orchestration is delegated to
         * `OpenSeadragon.FlexRenderer#render`.
         *
         * @override
         * @param {Array<OpenSeadragon.TiledImage>} tiledImages - TiledImage objects to draw.
         * @param {object} [view=undefined] - Optional custom view state.
         * @param {OpenSeadragon.Rect} view.bounds - Viewport bounds.
         * @param {OpenSeadragon.Point} view.center - Viewport center.
         * @param {number} view.rotation - Viewport rotation in radians.
         * @param {number} view.zoom - Viewport zoom.
         * @returns {void}
         *
         * @memberof OpenSeadragon.FlexDrawer#
         */
        draw(tiledImages, view = undefined) {
            if (!tiledImages || tiledImages.length === 0) {
                this.renderer.clear();
                return;
            }

            if (!this._drawReady && !this._refreshDrawReadyState()) {
                return;
            }

            view = this._resolveRenderView(view);

            // TODO consider sending data and computing on GPU.
            // calculate view matrix for viewer
            const flipMultiplier = this.viewport.flipped ? -1 : 1;
            const posMatrix = $.Mat3.makeTranslation(-view.center.x, -view.center.y);
            const scaleMatrix = $.Mat3.makeScaling(2 / view.bounds.width * flipMultiplier, -2 / view.bounds.height);
            const rotMatrix = $.Mat3.makeRotation(-view.rotation);
            const viewMatrix = scaleMatrix.multiply(rotMatrix).multiply(posMatrix);

            this._ensurePackLayout();
            this._ensureOffscreenCapacity();

            const firstPass = this._collectFirstPassPayload(tiledImages, view, viewMatrix);
            const secondPass = this._collectSecondPassPayload(view);

            this.renderer.render({
                firstPass: firstPass,
                secondPass: secondPass
            });
        } // end of function

        /**
         * Build first-pass render packages from OpenSeadragon TiledImage state.
         *
         * This method is the OpenSeadragon-to-renderer adaptation step for the first
         * pass. It collects drawable tiles, extracts renderer-ready cache payloads,
         * computes tile transform matrices, and converts crop/clip polygons into
         * viewport-coordinate polygons consumed by the active backend.
         *
         * It intentionally does not clear WebGL state and does not execute the first
         * pass. The normal draw path passes the returned packages to
         * `OpenSeadragon.FlexRenderer#render`.
         *
         * @private
         * @param {Array<OpenSeadragon.TiledImage>} tiledImages - TiledImage objects to draw.
         * @param {object} viewport - Resolved viewport state.
         * @param {OpenSeadragon.Rect} viewport.bounds - Viewport bounds.
         * @param {OpenSeadragon.Point} viewport.center - Viewport center.
         * @param {number} viewport.rotation - Viewport rotation in radians.
         * @param {number} viewport.zoom - Viewport zoom.
         * @param {OpenSeadragon.Mat3} viewMatrix - Matrix mapping viewport coordinates into renderer clip space.
         * @returns {Array<FPRenderPackage>} First-pass render packages.
         *
         * @memberof OpenSeadragon.FlexDrawer#
         */
        _collectFirstPassPayload(tiledImages, viewport, viewMatrix) {
            // FIRST PASS (render things as they are into the corresponding off-screen textures)
            const TI_PAYLOAD = [];

            for (let tiledImageIndex = 0; tiledImageIndex < tiledImages.length; tiledImageIndex++) {
                const tiledImage = tiledImages[tiledImageIndex];
                const payload = [];
                const vecPayload = [];
                const diagnosticPayload = [];

                const tilesToDraw = tiledImage.getTilesToDraw();

                // rendering in 4 overlapping groups of non-overlapping tiles so the depth value stays relatively small
                // TODO: move the tile ordering elsewhere to reduce amount of time spent recomputing it - possibly to TiledImage
                tilesToDraw.sort(
                    (entryA, entryB) => {
                        let levelA = entryA.tile.level;
                        let levelOrderA = 2 * (entryA.tile.y % 2) + (entryA.tile.x % 2);

                        let levelB = entryB.tile.level;
                        let levelOrderB = 2 * (entryB.tile.y % 2) + (entryB.tile.x % 2);

                        if (levelA === levelB) {
                            return levelOrderB - levelOrderA;
                        }

                        return levelB - levelA;
                    }
                );

                let overallMatrix = viewMatrix;
                let imageRotation = tiledImage.getRotation(true);
                // if needed, handle the tiledImage being rotated

                // todo consider in-place multiplication, this creates insane amout of arrays
                if (imageRotation % 360 !== 0) {
                    let imageRotationMatrix = $.Mat3.makeRotation(-imageRotation * Math.PI / 180);
                    let imageCenter = tiledImage.getBoundsNoRotate(true).getCenter();
                    let t1 = $.Mat3.makeTranslation(imageCenter.x, imageCenter.y);
                    let t2 = $.Mat3.makeTranslation(-imageCenter.x, -imageCenter.y);

                    // update the view matrix to account for this image's rotation
                    let localMatrix = t1.multiply(imageRotationMatrix).multiply(t2);
                    overallMatrix = viewMatrix.multiply(localMatrix);
                }

                if (tiledImage.getOpacity() > 0 && tilesToDraw.length > 0) {
                    // TODO support placeholder?
                    // if (tiledImage.placeholderFillStyle && tiledImage._hasOpaqueTile === false) {
                    //     this._drawPlaceholder(tiledImage);
                    // }

                    for (let tileIndex = 0; tileIndex < tilesToDraw.length; ++tileIndex) {
                        const tile = tilesToDraw[tileIndex].tile;

                        const tileInfo = this.getDataToDraw(tile);
                        if (!tileInfo) {
                            continue;
                        }

                        if (this._isDiagnosticTileInfo(tileInfo)) {
                            diagnosticPayload.push(this._makeTileDiagnosticRegion(
                                tile,
                                tiledImage,
                                overallMatrix,
                                tileInfo.reason || "invalid-data"
                            ));
                            continue;
                        }

                        const transformMatrix = this._updateTileMatrix(tileInfo, tile, tiledImage, overallMatrix);

                        if (tileInfo.texture) {
                            payload.push({
                                transformMatrix,
                                dataIndex: (typeof tiledImage.__flexBaseLayer === "number") ? tiledImage.__flexBaseLayer : tiledImageIndex, // color layer index
                                stencilIndex: tiledImageIndex,
                                texture: tileInfo.texture,
                                position: tileInfo.position,
                                normalized: tileInfo.normalized !== false,
                                tile: tile
                            });
                        } else if (tileInfo.vectors) {
                            // Flatten vector meshes into a simple draw list.

                            if (tileInfo.vectors.fills) {
                                tileInfo.vectors.fills.matrix = transformMatrix;
                            }
                            if (tileInfo.vectors.lines) {
                                tileInfo.vectors.lines.matrix = transformMatrix;
                            }
                            if (tileInfo.vectors.linePrimitives) {
                                for (const lineBatch of tileInfo.vectors.linePrimitives) {
                                    lineBatch.matrix = transformMatrix;
                                }
                            }
                            if (tileInfo.vectors.points) {
                                tileInfo.vectors.points.matrix = transformMatrix;
                            }

                            vecPayload.push(tileInfo.vectors);
                        } else if (tileInfo.__flexEmpty) {
                            // Legitimately empty vector tile (no geometry overlapped it):
                            // nothing to draw, and NOT an error — never tint it.
                            continue;
                        } else {
                            diagnosticPayload.push(this._makeTileDiagnosticRegion(
                                tile,
                                tiledImage,
                                overallMatrix,
                                "invalid-data"
                            ));
                        }
                    }
                }

                let polygons;

                //TODO: osd could cache this.getBoundsNoRotate(current) which might be fired many times in rendering (possibly also other parts)
                if (tiledImage._croppingPolygons) {
                    polygons = tiledImage._croppingPolygons.map(polygon => polygon.flatMap(coord => {
                        let point = tiledImage.imageToViewportCoordinates(coord.x, coord.y, true);
                        return [point.x, point.y];
                    }));
                } else {
                    polygons = [];
                }

                if (tiledImage._clip) {
                    const polygon = [
                        {x: tiledImage._clip.x, y: tiledImage._clip.y},
                        {x: tiledImage._clip.x + tiledImage._clip.width, y: tiledImage._clip.y},
                        {x: tiledImage._clip.x + tiledImage._clip.width, y: tiledImage._clip.y + tiledImage._clip.height},
                        {x: tiledImage._clip.x, y: tiledImage._clip.y + tiledImage._clip.height},
                    ];
                    polygons.push(polygon.flatMap(coord => {
                        let point = tiledImage.imageToViewportCoordinates(coord.x, coord.y, true);
                        return [point.x, point.y];
                    }));
                }

                const packCount = tiledImage.__flexPackCount || 1;
                const baseLayer =
                    (typeof tiledImage.__flexBaseLayer === "number") ? tiledImage.__flexBaseLayer : tiledImageIndex;

                for (let packIndex = 0; packIndex < packCount; packIndex++) {
                    TI_PAYLOAD.push({
                        tiles: payload,
                        vectors: vecPayload,
                        diagnostics: diagnosticPayload,
                        polygons: polygons,
                        dataIndex: baseLayer + packIndex,
                        stencilIndex: tiledImageIndex,
                        packIndex: packIndex,
                        _temp: overallMatrix, // todo dirty
                    });
                }
            }

            // todo flatten render data

            return TI_PAYLOAD;
        }

        /**
         * Collects shader layer variables (opacity, pixelSize, zoom) into one flat array,
         * group shader layers are followed by their child layers in the order specified by the group
         * @param shaders
         * @param shaderOrder
         * @param viewport
         * @returns {*[]}
         * @private
         */
        _collectShaderUniforms(shaders, shaderOrder, viewport) {
            const sources = [];
            const flatShaders = this.renderer.getFlatShaderLayers(shaders, shaderOrder);

            const canvas = this.renderer.getPresentationCanvas();
            const osdViewport = this.viewer.viewport;
            const inner = osdViewport && osdViewport._containerInnerSize;
            const sx = inner && inner.x ? canvas.width / inner.x : 1;
            const sy = inner && inner.y ? canvas.height / inner.y : 1;

            for (const shader of flatShaders) {
                const config = shader.getConfig();
                const hasSources = Array.isArray(config.tiledImages) && config.tiledImages.length > 0;
                const tiledImage = hasSources ? this.viewer.world.getItemAt(config.tiledImages[0]) : null;

                let imageOriginPx = [0, 0];
                if (tiledImage && osdViewport) {
                    // image (0,0) → viewport coords → CSS viewer-element pixels (top-down)
                    // → framebuffer pixels (bottom-up to match gl_FragCoord).
                    const vp = tiledImage.imageToViewportCoordinates(0, 0, true);
                    const cssPt = osdViewport.pixelFromPoint(vp, true);
                    imageOriginPx[0] = cssPt.x * sx;
                    imageOriginPx[1] = canvas.height - cssPt.y * sy;
                }

                // Mind the units: imageOriginPx is framebuffer px (sx/sy applied above), but
                // pixelSize is CSS px per image px — _tiledImageViewportToImageZoom divides by
                // _containerInnerSize, which is CSS. Shaders that divide one by the other must
                // bridge them with devicePixelScale, or their geometry comes out 1/DPR-sized.
                //
                // Per-axis, because the framebuffer size is rounded per axis: 1634x1586 CSS at
                // DPR 1.2 gives 1961x1903, so sx != sy. imageOriginPx.x carries sx and .y carries
                // sy, and the divisor has to match the component it divides.
                sources.push({
                    zoom: viewport.zoom,
                    pixelSize: tiledImage ? this._tiledImageViewportToImageZoom(tiledImage, viewport.zoom) : 1,
                    devicePixelScale: [sx, sy],
                    opacity: tiledImage ? tiledImage.getOpacity() : 1,
                    imageOriginPx,
                    shader: shader,
                });
            }

            return sources;
        }

        /**
         * Build the second-pass render package array from the renderer's current
         * ShaderLayer graph and the resolved OpenSeadragon viewport state.
         *
         * This remains drawer-owned because pixel size and opacity are currently
         * derived from OpenSeadragon `TiledImage` instances.
         *
         * @private
         * @param {object} viewport - Resolved viewport state.
         * @param {number} viewport.zoom - Viewport zoom.
         * @returns {Array<SPRenderPackage>} Second-pass render packages.
         *
         * @memberof OpenSeadragon.FlexDrawer#
         */
        _collectSecondPassPayload(viewport) {
            return this._collectShaderUniforms(
                this.renderer.getAllShaders(),
                this.renderer.getShaderLayerOrder(),
                viewport
            );
        }

        _getTileRenderMeta(tile, tiledImage) {
            let result = tile._renderStruct;
            if (result) {
                return result;
            }

            // Overlap fraction of tile if set
            let overlap = tiledImage.source.tileOverlap;
            if (overlap > 0) {
                let nativeWidth = tile.sourceBounds.width; // in pixels
                let nativeHeight = tile.sourceBounds.height; // in pixels
                let overlapWidth  = (tile.x === 0 ? 0 : overlap) + (tile.isRightMost ? 0 : overlap); // in pixels
                let overlapHeight = (tile.y === 0 ? 0 : overlap) + (tile.isBottomMost ? 0 : overlap); // in pixels
                let widthOverlapFraction = overlap / (nativeWidth + overlapWidth); // as a fraction of image including overlap
                let heightOverlapFraction = overlap / (nativeHeight + overlapHeight); // as a fraction of image including overlap
                tile._renderStruct = result = {
                    overlapX: widthOverlapFraction,
                    overlapY: heightOverlapFraction
                };
            } else {
                tile._renderStruct = result = {
                    overlapX: 0,
                    overlapY: 0
                };
            }

            return result;
        }

        /**
         * Get transform matrix that will be applied to tile.
         */
        _updateTileMatrix(tileInfo, tile, tiledImage, viewMatrix){
            let tileMeta = this._getTileRenderMeta(tile, tiledImage);
            let xOffset = tile.positionedBounds.width * tileMeta.overlapX;
            let yOffset = tile.positionedBounds.height * tileMeta.overlapY;

            let x = tile.positionedBounds.x + (tile.x === 0 ? 0 : xOffset);
            let y = tile.positionedBounds.y + (tile.y === 0 ? 0 : yOffset);
            let right = tile.positionedBounds.x + tile.positionedBounds.width - (tile.isRightMost ? 0 : xOffset);
            let bottom = tile.positionedBounds.y + tile.positionedBounds.height - (tile.isBottomMost ? 0 : yOffset);

            const model = new $.Mat3([
                right - x, 0, 0, // sx = width
                0, bottom - y, 0, // sy = height
                x, y, 1
            ]);

            if (tile.flipped) {
                // For documentation:
                // // - flips the tile so that we see it's back
                // const flipLeftAroundTileOrigin = $.Mat3.makeScaling(-1, 1);
                // //  tile's geometry stays the same so when looking at it's back we gotta reverse the logic we would normally use
                // const moveRightAfterScaling = $.Mat3.makeTranslation(-1, 0);
                // matrix = matrix.multiply(flipLeftAroundTileOrigin).multiply(moveRightAfterScaling);

                //Optimized:
                model.scaleAndTranslateSelf(-1, 1, 1, 0);
            }

            model.scaleAndTranslateOtherSetSelf(viewMatrix);
            return model.values;
        }

        /**
         * Get pixel size value.
         */
        _tiledImageViewportToImageZoom(tiledImage, viewportZoom) {
            var ratio = tiledImage._scaleSpring.current.value *
                tiledImage.viewport._containerInnerSize.x /
                tiledImage.source.dimensions.x;
            return ratio * viewportZoom;
        }

        //todo: this could be called only on change of TI, not each frame!
        _updatePackLayout() {
            const world = this.viewer.world;
            const itemCount = world.getItemCount();

            let baseLayer = [];
            let packCount = [];
            let total = 0;

            for (let i = 0; i < itemCount; i++) {
                const ti = world.getItemAt(i);
                const pc = ti && ti.__flexPackCount ? ti.__flexPackCount : 1;
                baseLayer[i] = total;
                packCount[i] = pc;
                total += pc;
                ti.__flexBaseLayer = baseLayer[i];
            }

            if (!this.renderer.__flexPackInfo) {
                this.renderer.__flexPackInfo = { packCount: [], channelCount: [], componentsPerPack: [] };
            }
            this.renderer.__flexPackInfo.layout = {
                baseLayer: baseLayer,
                packCount: packCount,
                totalLayers: total
            };

            // TODO: is this refreshing logic necessary?
            //  this.renderer.__flexPackInfo.version =
            //     (this.renderer.__flexPackInfo.version || 0) + 1;
        }

        /**
         * @returns {Boolean} true
         */
        canRotate() {
            return true;
        }

        /**
         * @returns {Boolean} true if canvas and webgl are supported
         */
        static isSupported(options = {}) {
            const rendererClass = $.FlexRenderer;
            if (rendererClass && typeof rendererClass.ensureRuntimeSupport === "function") {
                try {
                    return !!rendererClass.ensureRuntimeSupport({
                        webGLPreferredVersion: options.webGLPreferredVersion || "2.0",
                        force: options.force === true,
                        throwOnFailure: false,
                        debug: !!options.debug,
                    }).ok;
                } catch (e) {
                    return false;
                }
            }

            let canvasElement = document.createElement('canvas');
            let webglContext = $.isFunction(canvasElement.getContext) &&
                canvasElement.getContext('webgl');
            let ext = webglContext && webglContext.getExtension('WEBGL_lose_context');
            if (ext) {
                ext.loseContext();
            }
            return !!(webglContext);
        }

        /**
         * Creates an HTML element into which will be drawn.
         * @private
         * @returns {HTMLCanvasElement} the canvas to draw into
         */
        _createDrawingElement() {
            // Navigator has viewer parent reference
            // todo: what about reference strip??
            this._isNavigatorDrawer = !!this.viewer.viewer;
            if (this._isNavigatorDrawer) {
                this.options.debug = false;
                this.options.handleNavigator = false;
            }

            // todo better handling, build-in ID does not comply to syntax... :/
            this._id = this.constructor.idGenerator;

            // SETUP FlexRenderer
            const rendererOptions = $.extend(
                // Default
                {
                    debug: false,
                    webGLPreferredVersion: "2.0",
                    sharedContextKey: null,
                },
                // User-defined
                this.options,
                // Required
                {
                    redrawCallback: () => {
                        // Shader-internal UI controls (color picker, sliders, opacity, etc.)
                        // mutate this drawer's shader instances directly and route the redraw
                        // through here. The navigator's drawer has its own shader instances
                        // with their own state, so we mirror the change AND kick the navigator's
                        // own animation loop — without nav.forceRedraw(), glDrawing() never runs
                        // on the nav controls and the _needsLoad=true left by .set() never flushes.
                        // Gated on the (opt-in, default-true) handleNavigator option; skipped on
                        // the navigator drawer itself which has handleNavigator forced to false.
                        if (this.options.handleNavigator && this.viewer.navigator) {
                            this._syncNavigatorShaderState();
                            this.viewer.navigator.forceRedraw();
                        }
                        this.viewer.forceRedraw();
                    },
                    refetchCallback: (request) => this._handleRefetchRequest(request),
                    uniqueId: "osd_" + this._id,
                    sharedContextKey: this.options.sharedContextKey,
                    // TODO: problem when navigator renders first
                    // Navigator must not have the handler since it would attempt to define the controls twice
                    htmlHandler: this._isNavigatorDrawer ? null : this.options.htmlHandler,
                    // However, navigator must have interactive same as parent renderer to bind events to the controls
                    interactive: this._isNavigatorDrawer ? false : !!this.options.htmlHandler,
                    canvasOptions: {
                        stencil: true
                    }
                });
            this.renderer = new $.FlexRenderer(rendererOptions);
            this.renderer.drawer = this;

            this.renderer.setDataBlendingEnabled(true); // enable alpha blending
            this.webGLVersion = this.renderer.webglVersion;
            this.debug = rendererOptions.debug;

            const canvas = this.renderer.getPresentationCanvas();
            let viewportSize = this._calculateCanvasSize();

            // SETUP CANVASES
            this._setupCanvases();

            canvas.width = viewportSize.x;
            canvas.height = viewportSize.y;
            this._refreshDrawReadyState();

            this._interactionOptions = this._normalizeInteractionOptions(
                this._isNavigatorDrawer ? false : this.options.interaction
            );

            if (this._interactionOptions.enabled) {
                this.setInteractionOptions(this._interactionOptions, {
                    notify: true,
                    redraw: false,
                    reason: "drawer-init-interaction"
                });
            }

            return canvas;
        }

        /**
         * Sets whether image smoothing is enabled or disabled.
         * @param {Boolean} enabled if true, uses gl.LINEAR as the TEXTURE_MIN_FILTER and TEXTURE_MAX_FILTER, otherwise gl.NEAREST
         */
        setImageSmoothingEnabled(enabled){
            if( this._imageSmoothingEnabled !== enabled ){
                this._imageSmoothingEnabled = enabled;
                this.setInternalCacheNeedsRefresh();
                this.viewer.requestInvalidate(false);
            }
        }

        /**
         * Override the image-smoothing flag for a single tiledImage. Falls back to the
         * drawer-wide value when undefined.
         *
         * Note: the sampler filter is baked into prepared textures at upload time, and
         * OpenSeadragon's tile cache is keyed by tile content, not tiledImage identity.
         * If two tiledImages reference the same source tiles, they will share the
         * cached prepared textures — the first uploader wins the filter. In the common
         * case where the per-source flag matches the source's identity, this is fine;
         * setInternalCacheNeedsRefresh() forces re-preparation when the flag flips.
         *
         * @param {OpenSeadragon.TiledImage} tiledImage
         * @param {Boolean|null|undefined} enabled true → gl.LINEAR, false → gl.NEAREST,
         *     null/undefined → inherit drawer default
         */
        setTiledImageSmoothingEnabled(tiledImage, enabled){
            if (!tiledImage) {
                return;
            }
            const normalized = enabled === null || enabled === undefined ? undefined : !!enabled;
            if (tiledImage.__flexImageSmoothingEnabled === normalized) {
                return;
            }
            tiledImage.__flexImageSmoothingEnabled = normalized;
            this.setInternalCacheNeedsRefresh();
            if (typeof tiledImage.requestInvalidate === "function") {
                tiledImage.requestInvalidate(true);
            } else {
                this.viewer.requestInvalidate(false);
            }
        }

        /**
         * Resolve the effective image-smoothing flag for a tiledImage, honoring the
         * per-tiledImage override when present.
         *
         * @private
         * @param {OpenSeadragon.TiledImage} [tiledImage]
         * @returns {Boolean}
         */
        _resolveImageSmoothingEnabled(tiledImage){
            const override = tiledImage && tiledImage.__flexImageSmoothingEnabled;
            if (override === true || override === false) {
                return override;
            }
            return !!this._imageSmoothingEnabled;
        }

        internalCacheCreate(cache, tile) {
            const tiledImage = tile.tiledImage;
            const normalized = this._normalizeCacheData(cache);

            return this.createTileInfoFromSource({
                data: normalized.data,
                type: normalized.type,
                tile,
                tiledImage
            }).catch(error => {
                $.console.error(`Failed to prepare tile data.`, error, normalized.data);

                return this._createDiagnosticTileInfo(
                    error && error.reason ? error.reason : "invalid-data"
                );
            });
        }

        /**
         * Create an internal tile-info sentinel for tile data that was received but
         * could not be converted into renderer-ready raster or vector data.
         *
         * @private
         * @param {string} reason
         * @return {{__flexDiagnostic: boolean, reason: string}}
         */
        _createDiagnosticTileInfo(reason = "invalid-data") {
            return {
                __flexDiagnostic: true,
                reason: reason
            };
        }

        /**
         * Return whether tile info is an internal diagnostic sentinel.
         *
         * @private
         * @param {*} tileInfo
         * @return {boolean}
         */
        _isDiagnosticTileInfo(tileInfo) {
            return !!(tileInfo && tileInfo.__flexDiagnostic === true);
        }

        /**
         * Return renderer-neutral texture options for prepared tile resources.
         *
         * @private
         * @returns {RasterTileTextureOptions}
         */
        _getPreparedTileTextureOptions(tiledImage) {
            return {
                imageSmoothingEnabled: this._resolveImageSmoothingEnabled(tiledImage)
            };
        }

        /**
         * Convert a successful renderer preparation result into FlexDrawer's
         * internal raster tile-info shape.
         *
         * FlexDrawer owns OpenSeadragon tile placement. The renderer/backend owns
         * the prepared resource.
         *
         * @private
         * @param {PreparedRasterTileSuccess} result - Successful preparation result.
         * @param {OpenSeadragon.Tile} tile - OpenSeadragon tile.
         * @param {OpenSeadragon.TiledImage} tiledImage - Owning tiled image.
         * @returns {{position: Float32Array, texture: *, resource: *, vectors: undefined}}
         */
        _createPreparedRasterTileInfo(result, tile, tiledImage) {
            return {
                position: this._computeTilePosition(tile, tiledImage, result.width, result.height),
                texture: result.texture,
                resource: result.resource,
                // false only for float-formatted packs; drives the first-pass [0,1] clamp
                normalized: result.normalized !== false,
                vectors: undefined
            };
        }

        /**
         * Convert a preparation failure into the existing diagnostic tile sentinel.
         *
         * @private
         * @param {PreparedTileFailure} result - Failed preparation result.
         * @returns {{__flexDiagnostic: boolean, reason: string}}
         */
        _createDiagnosticTileInfoFromPreparationFailure(result) {
            const reason = result && result.reason ? result.reason : "invalid-data";
            return this._createDiagnosticTileInfo(reason);
        }

        /**
         * Return whether OpenSeadragon/canvas preflight can already identify a
         * bitmap-like tile source as tainted.
         *
         * This is an adapter-level optimization only. It does not replace renderer/backend upload classification.
         *
         * @private
         * @param {*} data - Normalized tile data.
         * @param {OpenSeadragon.TiledImage} tiledImage - Owning tiled image.
         * @returns {boolean}
         */
        _isKnownTaintedBitmapTileData(data, tiledImage) {
            if (tiledImage && typeof tiledImage.isTainted === "function" && tiledImage.isTainted()) {
                return true;
            }

            const canvas = this._getCanvasFromBitmapTileData(data);

            if (!canvas) {
                return false;
            }

            // checks if the canvas is tainted by trying to read from it
            return !!$.isCanvasTainted(canvas);
        }

        /**
         * Extract a canvas from bitmap-like tile data when available.
         *
         * @private
         * @param {*} data - Tile data.
         * @returns {HTMLCanvasElement | OffscreenCanvas | null}
         */
        _getCanvasFromBitmapTileData(data) {
            if (!data) {
                return null;
            }

            if (typeof CanvasRenderingContext2D !== "undefined" && data instanceof CanvasRenderingContext2D) {
                return data.canvas || null;
            }

            if (typeof HTMLCanvasElement !== "undefined" && data instanceof HTMLCanvasElement) {
                return data;
            }

            if (typeof OffscreenCanvas !== "undefined" && data instanceof OffscreenCanvas) {
                return data;
            }

            return null;
        }

        async createTileInfoFromSource({ data, type, tile, tiledImage }) {
            if (type === "undefined") {
                return null;
            }

            if (type === "vector-mesh" || (data && (data.fills || data.lines || data.linePrimitives || data.points || data.__suspicious))) {
                // The worker flags a tile where geometry with real coverage overlapped yet
                // nothing meshed. That is "data expected here, none produced" — surface it as
                // a diagnostic (amber when diagnostics are on) rather than a silent blank.
                if (data && data.__suspicious) {
                    return this._createDiagnosticTileInfo("expected-data-missing");
                }

                const result = await this.renderer.prepareVectorTile({
                    data: data
                });

                if (!result.ok) {
                    return this._createDiagnosticTileInfoFromPreparationFailure(result);
                }

                return {
                    position: null,
                    texture: null,
                    resource: result.resource,
                    vectors: result.vectors,
                    // ok but nothing uploaded: a genuinely empty (no-data) tile. Marked so the
                    // draw loop skips it silently instead of tinting it as invalid.
                    __flexEmpty: !result.vectors
                };
            }

            const isGpuTextureSet = type === "gpuTextureSet" || (data && typeof data.getType === "function" && data.getType() === "gpuTextureSet");

            if (isGpuTextureSet) {
                const result = await this.renderer.prepareGpuTextureTile({
                    data: data,
                    textureOptions: this._getPreparedTileTextureOptions(tiledImage)
                });

                if (!result.ok) {
                    return this._createDiagnosticTileInfoFromPreparationFailure(result);
                }

                const gpuPackCount = result.packCount || result.textureDepth || 1;
                const gpuComponentsPerPack = result.componentsPerPack || 4;

                this._updatePackMetadata(
                    tiledImage,
                    gpuPackCount,
                    result.channelCount || gpuPackCount * gpuComponentsPerPack,
                    // `normalized: false` means the upload keeps raw float values -- exactly the
                    // data that an RGBA8 first-pass target would quantize and clamp away.
                    result.normalized === false ? "float16" : "unorm8",
                    gpuComponentsPerPack
                );

                if (this._packLayoutDirty) {
                    // TODO: is this refreshing logic necessary?
                    //  this._refreshPackLayoutNow();
                    this._packLayoutDirty = false;
                    this._requestRebuild();
                }

                return this._createPreparedRasterTileInfo(result, tile, tiledImage);
            }

            if (this._isKnownTaintedBitmapTileData(data, tiledImage)) {
                return this._createDiagnosticTileInfo("tainted-data");
            }

            const result = await this.renderer.prepareBitmapTile({
                data: data,
                textureOptions: this._getPreparedTileTextureOptions(tiledImage)
            });

            if (!result.ok) {
                return this._createDiagnosticTileInfoFromPreparationFailure(result);
            }

            // Bitmap tiles are always 8-bit unorm; stated rather than left undefined so an
            // image swapped from a float source back to a bitmap one releases the upgrade.
            this._updatePackMetadata(
                tiledImage,
                result.packCount || 1,
                result.channelCount || 4,
                "unorm8"
            );

            return this._createPreparedRasterTileInfo(result, tile, tiledImage);
        }

        // _refreshPackLayoutNow() {
        //     this._updatePackLayout();
        //     this._packLayoutDirty = false;
        // }

        /**
         * Compute fallback dimensions for a diagnostic tile region.
         *
         * Diagnostic entries have no decoded image/texture dimensions, so this uses
         * source bounds when OpenSeadragon exposes them and falls back to the tile
         * source's nominal tile dimensions.
         *
         * @private
         * @param {OpenSeadragon.Tile} tile
         * @param {OpenSeadragon.TiledImage} tiledImage
         * @return {{width: number, height: number}}
         */
        _getDiagnosticTileDimensions(tile, tiledImage) {
            const source = tiledImage && tiledImage.source ? tiledImage.source : {};
            const sourceBounds = tile && tile.sourceBounds ? tile.sourceBounds : null;
            const width = sourceBounds && Number.isFinite(sourceBounds.width) && sourceBounds.width > 0 ?
                sourceBounds.width :
                source.tileWidth || source.tileSize || 1;
            const height = sourceBounds && Number.isFinite(sourceBounds.height) && sourceBounds.height > 0 ?
                sourceBounds.height :
                source.tileHeight || source.tileSize || width;

            return {
                width: width,
                height: height
            };
        }

        /**
         * Create a renderer-ready diagnostic first-pass region for a tile.
         *
         * @private
         * @param {OpenSeadragon.Tile} tile
         * @param {OpenSeadragon.TiledImage} tiledImage
         * @param {OpenSeadragon.Mat3} overallMatrix
         * @param {string} [reason="invalid-data"]
         * @return {FPRenderDiagnosticTile}
         */
        _makeTileDiagnosticRegion(tile, tiledImage, overallMatrix, reason = "invalid-data") {
            const dimensions = this._getDiagnosticTileDimensions(tile, tiledImage);
            const position = this._computeTilePosition(
                tile,
                tiledImage,
                dimensions.width,
                dimensions.height
            );
            const diagnosticTileInfo = {
                position: position
            };

            return {
                reason: reason,
                transformMatrix: this._updateTileMatrix(
                    diagnosticTileInfo,
                    tile,
                    tiledImage,
                    overallMatrix
                ),
                position: position
            };
        }

        /**
         * Compute normalized tile texture coordinates (UVs) in source image space,
         * including overlap trimming. Works for both normal images and gpuTextureSet.
         */
        _computeTilePosition(tile, tiledImage, dataWidth, dataHeight) {
            let sourceWidthFraction, sourceHeightFraction;

            if (tile.sourceBounds) {
                sourceWidthFraction = Math.min(tile.sourceBounds.width, dataWidth) / dataWidth;
                sourceHeightFraction = Math.min(tile.sourceBounds.height, dataHeight) / dataHeight;
            } else {
                sourceWidthFraction = 1;
                sourceHeightFraction = 1;
            }

            const overlap = tiledImage.source.tileOverlap;
            if (overlap > 0) {
                // calculate the normalized position of the rect to actually draw
                // discarding overlap.
                const tileMeta = this._getTileRenderMeta(tile, tiledImage);

                const left   = (tile.x === 0 ? 0 : tileMeta.overlapX) * sourceWidthFraction;
                const top    = (tile.y === 0 ? 0 : tileMeta.overlapY) * sourceHeightFraction;
                const right  = (tile.isRightMost ? 1 : 1 - tileMeta.overlapX) * sourceWidthFraction;
                const bottom = (tile.isBottomMost ? 1 : 1 - tileMeta.overlapY) * sourceHeightFraction;

                return new Float32Array([
                    left, bottom,
                    left, top,
                    right, bottom,
                    right, top
                ]);
            } else {
                return new Float32Array([
                    0, sourceHeightFraction,
                    0, 0,
                    sourceWidthFraction, sourceHeightFraction,
                    sourceWidthFraction, 0
                ]);
            }
        }

        _ensurePackLayout() {
            if (this._packLayoutDirty) {
                this._updatePackLayout();
                this._packLayoutDirty = false;
            }
        }

        /**
         * Allocate the offscreen texture arrays and remember the layer depths that were
         * requested. Every call that changes the array depth must go through here so the
         * cached depths stay authoritative for _ensureOffscreenCapacity.
         *
         * @param {number} width - Offscreen width in pixels.
         * @param {number} height - Offscreen height in pixels.
         * @param {number} colorLayers - colorTextureA layer count (Σ pack counts).
         * @param {number} stencilLayers - stencilTextureA layer count (world item count).
         * @private
         */
        _setOffscreenDimensions(width, height, colorLayers, stencilLayers) {
            this.renderer.setDimensions(0, 0, width, height, colorLayers, stencilLayers);
            this._allocatedColorLayers = colorLayers;
            this._allocatedStencilLayers = stencilLayers;
        }

        /**
         * Grow the offscreen texture arrays before the first pass attaches their layers.
         *
         * Layer indices (dataIndex/stencilIndex) are recomputed at render time by
         * _ensurePackLayout, but the arrays are otherwise only (re)allocated on the debounced
         * rebuild and on resize. Adding a source or a source reporting more packs raises the
         * indices first, so framebufferTextureLayer would attach a layer beyond the allocated
         * depth and leave the framebuffer incomplete (every clear/draw for that source then
         * fails -> the source renders blank until the next rebuild). Reallocating here closes
         * that lag. Grow-only: the debounced rebuild handles shrinking. The first pass repaints
         * these arrays every frame, so reallocating immediately before it loses nothing.
         *
         * @private
         */
        _ensureOffscreenCapacity() {
            const neededColor = this._computeOffscreenLayerCount();
            const neededStencil = this.viewer.world.getItemCount();
            const haveColor = this._allocatedColorLayers || 0;
            const haveStencil = this._allocatedStencilLayers || 0;

            if (neededColor > haveColor || neededStencil > haveStencil) {
                this._setOffscreenDimensions(
                    this.canvas.width,
                    this.canvas.height,
                    Math.max(neededColor, haveColor),
                    Math.max(neededStencil, haveStencil)
                );
            }
        }

        _computeOffscreenLayerCount() {
            const world = this.viewer.world;
            const items = world._items || [];
            let total = 0;

            for (let i = 0; i < items.length; i++) {
                const ti = items[i];
                const packCount = ti && ti.__flexPackCount ? ti.__flexPackCount : 1;
                total += packCount;
            }

            return Math.max(total, 1);
        }

        internalCacheFree(data) {
            if (!data) {
                return;
            }

            if (data.resource) {
                this.renderer.releasePreparedTileResource(data.resource);
                data.resource = null;
            } else {
                if (data.texture) {
                    this.renderer.releasePreparedTileResource(data.texture);
                }

                if (data.vectors) {
                    this.renderer.releasePreparedTileResource(data.vectors);
                }
            }

            data.texture = null;
            data.vectors = null;
        }

        // inside OpenSeadragon.FlexDrawer

        _normalizeCacheData(cache) {
            if (!cache || cache.type === "undefined") {
                return { type: "undefined", data: null };
            }

            let data = cache.data;
            if (data instanceof CanvasRenderingContext2D) {
                data = data.canvas;
            }

            return {
                type: cache.type,
                data
            };
        }

        /**
         * Record the precision a tile source declares *before* any of its tiles decode.
         *
         * Optional tile-source contract: a source that already knows its sample precision from
         * its header can say so, which spares the renderer a mid-load program rebuild when the
         * first float tile arrives. Purely an optimization — `_updatePackMetadata` is the
         * authoritative signal and will correct a source that guesses wrong.
         *
         * @param {OpenSeadragon.TiledImage} tiledImage
         * @private
         */
        _captureDeclaredDataPrecision(tiledImage) {
            const source = tiledImage && tiledImage.source;
            if (!source || typeof source.getTileDataPrecision !== "function") {
                return;
            }

            let declared;
            try {
                declared = source.getTileDataPrecision();
            } catch (e) {
                $.console.warn("FlexDrawer: tile source getTileDataPrecision() threw, ignoring.", e);
                return;
            }

            if (declared !== "unorm8" && declared !== "float16") {
                return;
            }

            tiledImage.__flexDataPrecision = declared;
            this._refreshDataPrecision(tiledImage);
        }

        /**
         * Aggregate the per-image data precision over the whole world and tell the renderer.
         *
         * Aggregated here rather than tracked in the renderer because only the drawer sees the
         * world, and recomputed by scanning rather than kept as a per-index array because world
         * indices shift on removal and reorder (the same reason `__flexPackInfo` goes stale).
         *
         * `candidate` covers detached images an off-screen drawer renders without ever adding
         * them to `viewer.world` (region mirrors). Those never produce a `remove-item`, so a
         * float verdict from one is sticky for the drawer's lifetime — deliberately: staying on
         * a float target costs memory, dropping off one costs correctness.
         *
         * @param {OpenSeadragon.TiledImage} [candidate] image that just reported, if any
         * @private
         */
        _refreshDataPrecision(candidate) {
            if (!this.renderer || typeof this.renderer.setDataCarriesHighPrecision !== "function") {
                return;
            }

            const world = this.viewer && this.viewer.world;
            const items = (world && world._items) || [];

            if (candidate && candidate.__flexDataPrecision === "float16" &&
                    (!world || world.getIndexOfItem(candidate) < 0)) {
                this._detachedFloatDataSeen = true;
            }

            const anyFloat = !!this._detachedFloatDataSeen ||
                items.some(item => item && item.__flexDataPrecision === "float16");
            this.renderer.setDataCarriesHighPrecision(anyFloat);
        }

        _updatePackMetadata(tiledImage, packCount, channelCount, dataPrecision, componentsPerPack = 4) {
            if (!tiledImage) {
                return;
            }

            if ((dataPrecision === "unorm8" || dataPrecision === "float16") &&
                tiledImage.__flexDataPrecision !== dataPrecision) {
                tiledImage.__flexDataPrecision = dataPrecision;
                this._refreshDataPrecision(tiledImage);
            }

            const metadataWasReady = !!tiledImage.__flexMetadataReady;
            let metadataChanged = !metadataWasReady;

            if (tiledImage.__flexPackCount !== packCount) {
                tiledImage.__flexPackCount = packCount;
                this._packLayoutDirty = true;
                metadataChanged = true;
            }
            if (tiledImage.__flexChannelCount !== channelCount) {
                tiledImage.__flexChannelCount = channelCount;
                this._packLayoutDirty = true;
                metadataChanged = true;
            }
            // Must take part in change detection: the components-per-pack value is baked into
            // the generated GLSL (the sampleChannel fast-path decision), so a source that goes
            // from unknown to R16F has to trigger a shader refresh like the counts do.
            if (tiledImage.__flexComponentsPerPack !== componentsPerPack) {
                tiledImage.__flexComponentsPerPack = componentsPerPack;
                this._packLayoutDirty = true;
                metadataChanged = true;
            }
            tiledImage.__flexMetadataReady = true;

            if (this.renderer && !this.renderer.__flexPackInfo) {
                this.renderer.__flexPackInfo = {
                    packCount: [],
                    channelCount: [],
                    componentsPerPack: [],
                };
            }

            if (this.renderer && this.renderer.__flexPackInfo && this.viewer.world) {
                const tiIndex = this.viewer.world.getIndexOfItem(tiledImage);
                if (tiIndex >= 0) {
                    if (!this.renderer.__flexPackInfo.componentsPerPack) {
                        this.renderer.__flexPackInfo.componentsPerPack = [];
                    }
                    this.renderer.__flexPackInfo.packCount[tiIndex] = packCount;
                    this.renderer.__flexPackInfo.channelCount[tiIndex] = channelCount;
                    this.renderer.__flexPackInfo.componentsPerPack[tiIndex] = componentsPerPack;
                }
            }

            if (metadataChanged) {
                this._refreshShadersForTiledImage(tiledImage);
            }
        }

        _refreshShadersForTiledImage(tiledImage) {
            if (!this.renderer || !this.viewer || !this.viewer.world || !tiledImage) {
                return;
            }

            const tiIndex = this.viewer.world.getIndexOfItem(tiledImage);
            if (tiIndex < 0) {
                return;
            }

            const idsToRefresh = [];
            this.renderer.forEachShaderLayer(undefined, undefined, shader => {
                const config = shader.getConfig();
                if (config && Array.isArray(config.tiledImages) && config.tiledImages.includes(tiIndex)) {
                    idsToRefresh.push(shader.id);
                }
            });

            if (!idsToRefresh.length) {
                return;
            }

            for (const shaderId of idsToRefresh) {
                this.renderer.refreshShaderLayer(shaderId, { rebuildProgram: false });
            }

            this._requestRebuild(0, true);
        }

        _setClip(){
            // no-op: called, handled during rendering from tiledImage data
        }
    }

    FlexDrawer._idGenerator = 0;
    Object.defineProperty(FlexDrawer, 'idGenerator', {
        get: function() {
            return this._idGenerator++;
        }
    });

    $.FlexDrawer = FlexDrawer;

}( OpenSeadragon ));
